import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../../types/screensTypes";
import * as Yup from "yup";
import {
    getVoices,
    createAudioQueueAI,
    getWaitingAudioAI,
    createProjectAI,
    addItemToProjectAI,
    createManualAudioAI,
} from "../../../api";
import AddToProjectModal from "../../../components/modals/AddToProjectModal";
import SelectVariantsModal from "../../../components/modals/SelectVariantsModal";
import AddToProjectButton from "../../../components/ui/AddToProjectButton";
import Button from "../../../components/ui/Button";
import SpeedSlider from "../../../components/ui/AudioSpeedSlider";
import { useGlobalState } from "../../../context/useGlobalState";
import { useNotification } from "../../../context/NotificationContext";
import AIGenerationStatusModal from "../../../components/modals/AIGenerationStatusModal";
import useCancelGeneration from "../../../hooks/useCancelGeneration";
import { Tooltip } from "@mui/material";
import { Field, Formik, useFormikContext } from "formik";
import { useVoiceResolvers } from "../../../hooks/useVoiceResolvers";
import { DropdownField, TextField } from "../../../components/common/FormFields";
import FormGroup from "../../../components/common/FormGroup";
import Dropdown from "../../../components/common/Dropdown";
import TextWithVoiceSelection from "../../../components/ui/TextWithVoiceSelection";
import AutoResizeTextField from "../../../components/common/AutoResizeTextField";
import MultiAudioPlayer from "../../../components/common/MultiAudioPlayer";
import Icon from "../../../components/common/Icon";
import playIcon from "../../../assets/images/play.svg";
import styles from "./TextToSpeech.module.css";
import {
    DialogLine,
    TextSegment,
    Capsule,
    normalizeDialogLines,
    parseLegacyCapsules,
    collectVoiceIds,
    convertSegmentsToDialogLines,
    calculateWordCount,
    calculateCapsuleCharacters,
    calculateTotalCharacters,
} from "./utils";
import { AI_MODEL_TYPES } from "../../../constants";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import { useRemainingTokens } from "../../../hooks/useRemainingTokens";
import { isQuotaError } from "../../../utils/quotaError";
import stepStyles from "../../track/create/createStep.module.css";

/** Status poll interval for TTS generation (3s balances UX vs load / WAF). */
const STATUS_POLL_INTERVAL_MS = 3000;
/** After this many consecutive failed status polls, show connection-unstable copy (not generation failed). */
const MAX_STATUS_POLL_FAILURES = 5;

/** ElevenLabs models supported by the Text-to-Dialogue API (conversation mode). */
const DIALOGUE_MODELS = ["ELEVENLABS_V3", "ELEVENLABS_V4"];
/** Default model for ElevenLabs voices and conversation mode. */
const DEFAULT_ELEVENLABS_MODEL = "ELEVENLABS_V4";
const supportsDialogue = (model?: string | null) => Boolean(model && DIALOGUE_MODELS.includes(model));
/** Per-request character limit for models that enforce one. */
const MODEL_CHAR_LIMITS: Record<string, number> = { ELEVENLABS_V3: 5000, ELEVENLABS_V4: 8000 };
const getModelCharLimit = (model?: string | null) => (model ? MODEL_CHAR_LIMITS[model] : undefined);
const isElevenLabsModel = (model?: string | null) =>
    model === "ELEVENLABS_V2" || model === "ELEVENLABS_V3" || model === "ELEVENLABS_V4";
const getElevenLabsModelOptions = (t: (key: string) => string) => [
    { value: "ELEVENLABS_V2", label: t("standardModel") },
    { value: "ELEVENLABS_V3", label: t("advancedModel") },
    { value: "ELEVENLABS_V4", label: t("studioModel") },
];

interface FormValues {
    textObject: string;
    selectedContentLanguage: string;
    selectedVoice: string;
    selectedSecondVoice: string;
    selectedTextType: "isText" | "isdialog";
    audioSpeed: number;
    audioModel: string;
    dialogLines: DialogLine[];
    useMultipleNarrators: boolean;
    podcastType: "narrative" | "conversation";
}

const TextToSpeechForm: React.FC<{
    handleAudioAIAwaiting: (
        id: string,
        setValues: (values: Partial<FormValues>) => void,
        opts?: { force?: boolean; silent?: boolean },
    ) => Promise<void>;
    id?: string;
    voiceOptions: any[];
    audioSpeed: number;
    onAudioSpeedChange: (speed: number) => void;
    onUpdateLineVoice?: (lineId: string, voiceId: string) => void;
    selectedVoices?: string[];
    onAddVoice?: (voiceId: string) => void;
    onRemoveVoice?: (voiceId: string) => void;
    hasTextSelection?: boolean;
    onAssignVoiceToSelection?: (voiceId: string) => void;
    lastAppliedVoice?: string | null;
    focusedCapsuleId?: string | null;
    capsules?: Capsule[];
    onCapsuleVoiceChange?: (capsuleId: string, voiceId: string) => void;
    onCapsuleSpeedChange?: (capsuleId: string, speed: number) => void;
    onCapsuleModelChange?: (capsuleId: string, model: string) => void;
    selectedAudioId?: string | null;
}> = ({
    handleAudioAIAwaiting,
    id,
    voiceOptions,
    audioSpeed,
    onAudioSpeedChange,
    onUpdateLineVoice,
    selectedVoices = [],
    onAddVoice,
    onRemoveVoice,
    hasTextSelection = false,
    onAssignVoiceToSelection,
    lastAppliedVoice,
    focusedCapsuleId,
    capsules = [],
    onCapsuleVoiceChange,
    onCapsuleSpeedChange,
    onCapsuleModelChange,
    selectedAudioId = null,
}) => {
    const { values, setValues, setFieldValue } = useFormikContext<FormValues>();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();

    const isDialogueModel = supportsDialogue(values.audioModel);
    const isNarrativeMode = values.podcastType === "narrative";

    // In narrative mode with focused capsule, use capsule's voice/speed; otherwise use form values
    const focusedCapsule = focusedCapsuleId && isNarrativeMode ? capsules.find((c) => c.id === focusedCapsuleId) : null;

    // Ref to track previous focused capsule ID to detect focus changes
    const prevFocusedCapsuleIdRef = useRef<string | null>(null);

    const currentVoiceId = focusedCapsule?.selectedVoice || values.selectedVoice;
    const currentSpeed = focusedCapsule?.audioSpeed ?? audioSpeed;
    const currentModel = focusedCapsule?.audioModel || values.audioModel;
    const selectedVoiceInfo = currentVoiceId ? voiceOptions.find((voice) => voice.value === currentVoiceId) : null;
    // Hide speed slider for ELEVENLABS_V3 in narrative mode when an audio is focused, and always for ELEVENLABS_V4 (speed not supported)
    const shouldShowSpeedSlider =
        Boolean(selectedVoiceInfo) &&
        currentModel !== "ELEVENLABS_V4" &&
        (values.audioModel !== "ELEVENLABS_V3" || (isNarrativeMode && !selectedAudioId));
    const [newVoiceToAdd, setNewVoiceToAdd] = useState<string>("");

    // Filter voices based on selected model
    const filteredVoiceOptions = useMemo(() => {
        // If voices are not loaded yet, return empty array to avoid filtering issues
        if (!voiceOptions || voiceOptions.length === 0) {
            return voiceOptions;
        }

        // Only filter voices in dialog mode (multiple narrators or multiple dialog lines)
        const isDialogMode = values.useMultipleNarrators || values.dialogLines.length > 1;

        if (!values.audioModel) {
            return voiceOptions;
        }

        const isDialogueVoiceModel = supportsDialogue(values.audioModel);
        let filtered: any[] = [];

        if (isDialogueVoiceModel && isDialogMode) {
            // Only show ElevenLabs voices, cloned voices, and prompt voices (all treated as ElevenLabs)
            filtered = voiceOptions.filter(
                (voice) =>
                    voice.origin === "elevenlabs" ||
                    (voice as any)?.isCloned === true ||
                    (voice as any)?.isPromptVoice === true,
            );
        } else {
            filtered = voiceOptions;
        }

        if (filtered.length === 0 && voiceOptions.length > 0 && values.audioModel) {
            return voiceOptions;
        }

        const formVoiceId = values.selectedVoice;
        if (formVoiceId && voiceOptions.length > 0) {
            const selectedVoiceInOptions = voiceOptions.find((voice) => voice.value === formVoiceId);
            if (selectedVoiceInOptions && !filtered.find((voice) => voice.value === formVoiceId)) {
                const isCompatible =
                    (isDialogueVoiceModel && selectedVoiceInOptions.origin === "elevenlabs") ||
                    (values.audioModel === "OPENAI" && selectedVoiceInOptions.origin === "openai");

                if (isCompatible) {
                    filtered = [selectedVoiceInOptions, ...filtered];
                }
            }
        }

        return filtered;
    }, [voiceOptions, values.useMultipleNarrators, values.dialogLines.length, values.audioModel, values.selectedVoice]);

    const getAvailableModelOptions = useMemo(() => {
        const isConversation = values.podcastType === "conversation";

        if (isConversation) {
            // Mode Conversation : modèles supportant Text-to-Dialogue (v3, v4)
            return getElevenLabsModelOptions(t).filter((option) => supportsDialogue(option.value));
        }

        // Use currentVoiceId (which may be from focused capsule) to determine available models
        const voiceInfo = currentVoiceId ? voiceOptions.find((voice) => voice.value === currentVoiceId) : null;

        // Mode Narratif simple : Standard, v3 et v4
        if (!voiceInfo) {
            return [{ value: "OPENAI", label: t("standardModel") }, ...getElevenLabsModelOptions(t)];
        }

        if (voiceInfo.origin === "openai") {
            return [{ value: "OPENAI", label: t("standardModel") }];
        } else {
            return getElevenLabsModelOptions(t);
        }
    }, [currentVoiceId, voiceOptions, values.podcastType, t]);

    // Force the default model when switching to Conversation mode (unless the model already supports dialogue)
    useEffect(() => {
        if (values.podcastType === "conversation" && !supportsDialogue(values.audioModel)) {
            setFieldValue("audioModel", DEFAULT_ELEVENLABS_MODEL);
        }
    }, [values.podcastType, values.audioModel, setFieldValue]);

    // Update audioSpeed in form when it changes externally
    useEffect(() => {
        if (values.audioSpeed !== audioSpeed) {
            setFieldValue("audioSpeed", audioSpeed);
        }
    }, [audioSpeed, setFieldValue, values.audioSpeed]);

    // Synchronize selectedVoice with currentVoiceId
    // In narrative mode with focused capsule, sync form-level voice with focused capsule's voice
    // This ensures the form-level voice stays in sync when focus changes
    // IMPORTANT: Only sync when focus changes, not when capsule voice changes (to avoid overwriting user selections)
    useEffect(() => {
        // Only sync when focus actually changes (not on every render)
        const currentFocusId: string | null = focusedCapsuleId ?? null;
        const focusChanged = prevFocusedCapsuleIdRef.current !== currentFocusId;
        prevFocusedCapsuleIdRef.current = currentFocusId;

        if (isNarrativeMode && focusedCapsuleId && focusedCapsule && focusChanged) {
            // In narrative mode with focused capsule, sync form-level voice with capsule's voice when focus changes
            // This ensures the dropdown shows the correct voice for the newly focused capsule
            if (focusedCapsule.selectedVoice) {
                setFieldValue("selectedVoice", focusedCapsule.selectedVoice, false);
            } else if (!focusedCapsule.selectedVoice && values.selectedVoice) {
                // If capsule has no voice but form has one, don't clear it (user might be about to assign it)
                // Only sync if capsule has a voice set
            }
        } else if (!isDialogueModel && !isNarrativeMode && currentVoiceId !== values.selectedVoice) {
            // In non-V3 mode and non-narrative mode, sync selectedVoice with currentVoiceId
            setFieldValue("selectedVoice", currentVoiceId || "");
        }
    }, [
        focusedCapsuleId,
        focusedCapsule,
        currentVoiceId,
        isDialogueModel,
        isNarrativeMode,
        values.selectedVoice,
        setFieldValue,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset dialog line voices if they're not compatible with the selected model
    useEffect(() => {
        // Don't reset if voices are not loaded yet
        if (voiceOptions.length === 0) {
            return;
        }

        if (values.audioModel && values.dialogLines.length > 0) {
            const isElevenLabs = isElevenLabsModel(values.audioModel);
            const needsUpdate = values.dialogLines.some((line) => {
                if (!line.voiceId) return false;
                const voiceInfo = voiceOptions.find((voice) => voice.value === line.voiceId);
                if (!voiceInfo) return false;
                return !(
                    (isElevenLabs && voiceInfo.origin === "elevenlabs") ||
                    (values.audioModel === "OPENAI" && voiceInfo.origin === "openai")
                );
            });

            if (needsUpdate) {
                const updatedLines = values.dialogLines.map((line) => {
                    if (!line.voiceId) return line;
                    const voiceInfo = voiceOptions.find((voice) => voice.value === line.voiceId);
                    if (!voiceInfo) return line;
                    const isCompatible =
                        (isElevenLabs && voiceInfo.origin === "elevenlabs") ||
                        (values.audioModel === "OPENAI" && voiceInfo.origin === "openai");
                    if (!isCompatible) {
                        return { ...line, voiceId: "" };
                    }
                    return line;
                });
                setValues({
                    ...values,
                    dialogLines: updatedLines,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.audioModel, values.dialogLines.length, voiceOptions]);

    // Automatically enable/disable multiple narrators mode based on model selection (only if there's one or zero dialog lines)
    useEffect(() => {
        // Don't change mode if there are multiple dialog lines or if dialogLines are already loaded
        if (values.dialogLines.length > 1) {
            return;
        }

        const isDialogueCapable = supportsDialogue(values.audioModel);
        const isConversation = values.podcastType === "conversation";

        if (isDialogueCapable && isConversation) {
            // Activate multiple narrators mode and initialize dialog lines if empty (only in conversation mode)
            if (!values.useMultipleNarrators) {
                setFieldValue("useMultipleNarrators", true);
            }
            if (values.dialogLines.length === 0) {
                const initialLine: DialogLine = {
                    id: `dialog-${Date.now()}-1`,
                    text: "",
                    voiceId: values.selectedVoice || "",
                };
                setFieldValue("dialogLines", [initialLine]);
            }
        } else {
            // Only deactivate multiple narrators mode if we have exactly one dialog line (not loaded from API)
            // Don't deactivate if we have multiple dialog lines (loaded from API) or if dialogLines are empty
            if (values.useMultipleNarrators && values.dialogLines.length === 1) {
                // Check if this is a loaded dialog line (has an id that looks like a database ID)
                // Database IDs from Prisma are typically CUIDs (25 chars) or UUIDs
                // Our generated IDs are like "dialog-1234567890-1" (shorter)
                const dialogLineId = values.dialogLines[0].id;
                const isLoadedDialogLine =
                    dialogLineId && (dialogLineId.length > 20 || !dialogLineId.startsWith("dialog-"));
                if (!isLoadedDialogLine) {
                    // Only deactivate if this is a locally created dialog line, not loaded from API
                    setFieldValue("useMultipleNarrators", false);
                    setFieldValue("dialogLines", []);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.audioModel, values.dialogLines.length, values.podcastType]);

    return (
        <div className={stepStyles.stepDescription}>
            <h3 className={styles.contentInfoTitle}>{t("contentInformation")}</h3>

            {/* Podcast Type */}
            <FormGroup name="podcastType" title={t("podcastType")} style={{ marginTop: 16 }} size="small" required>
                <Field
                    name="podcastType"
                    component={DropdownField}
                    options={[
                        { value: "narrative", label: t("podcastTypeNarrative") },
                        { value: "conversation", label: t("podcastTypeConversation") },
                    ]}
                    placeholder={t("selectPodcastType") || "Sélectionnez un type"}
                    isWithoutMargin={true}
                />
            </FormGroup>

            {/* Title */}
            <FormGroup name="textObject" title={t("title")} style={{ marginTop: 20 }} size="small" required>
                <Field name="textObject">
                    {({ field, form, meta }: any) => (
                        <TextField field={field} form={form} meta={meta} placeholder={t("title")} size="small" />
                    )}
                </Field>
            </FormGroup>

            {/* Language */}
            <FormGroup
                name="selectedContentLanguage"
                title={t("language")}
                style={{ marginTop: 20 }}
                size="small"
                required
            >
                <Field
                    name="selectedContentLanguage"
                    component={DropdownField}
                    options={data.languagesAI || []}
                    placeholder={t("selectALanguage")}
                    isWithoutMargin={true}
                />
            </FormGroup>

            {/* Voice + Model — green Réglages capsule frame (narrative only) */}
            {values.podcastType !== "conversation" && (
                <div className={styles.capGroup}>
                    <div className={styles.capGroupLabel}>
                        {t("settingsCapsule")}{" "}
                        {focusedCapsule && focusedCapsuleId
                            ? capsules.findIndex((c) => c.id === focusedCapsuleId) + 1
                            : 1}
                    </div>
                    {/* Voice - Only show in narrative mode */}
                    <FormGroup
                        name="selectedVoice"
                        title={t("voice")}
                        style={{ marginTop: 0, maxWidth: "100%" }}
                        size="small"
                        required
                    >
                        <Dropdown
                            options={filteredVoiceOptions}
                            placeholder={t("selectAVoice")}
                            isWithoutMargin={true}
                            value={currentVoiceId || ""}
                            onChange={(voiceId: string) => {
                                // If current model is incompatible with the newly selected voice, switch to a compatible default right away
                                const picked = voiceOptions.find((v) => v.value === voiceId);
                                if (picked) {
                                    const openAi = picked.origin === "openai";
                                    const compatibleOptions = openAi
                                        ? [{ value: "OPENAI", label: t("standardModel") }]
                                        : getElevenLabsModelOptions(t);

                                    const isCompatible = compatibleOptions.some(
                                        (opt) => opt.value === values.audioModel,
                                    );

                                    // In narrative mode, check if we should update form-level voice
                                    // In narrative mode with focused capsule: always update form-level to match the focused capsule
                                    // This ensures the form-level voice stays in sync with the focused capsule's voice
                                    const isNarrativeMode = values.podcastType === "narrative";
                                    // Always update form-level voice when a capsule is focused in narrative mode
                                    // This keeps form-level in sync with the focused capsule's voice
                                    const shouldUpdateFormVoice = !isNarrativeMode || focusedCapsuleId;

                                    // Set both voice and model together to avoid validation timing issues
                                    if (!isCompatible || !values.audioModel) {
                                        // Default to v4 for ElevenLabs voices; for OpenAI, just OPENAI
                                        const fallback = openAi ? "OPENAI" : DEFAULT_ELEVENLABS_MODEL;
                                        // Set both values atomically to ensure validation sees both values
                                        if (shouldUpdateFormVoice) {
                                            setValues({
                                                ...values,
                                                selectedVoice: voiceId,
                                                audioModel: fallback,
                                            });
                                        }
                                        // If shouldUpdateFormVoice is false, don't update form-level model either
                                        // The model will be updated per-capsule only
                                    } else {
                                        // Model is compatible
                                        if (shouldUpdateFormVoice) {
                                            setFieldValue("selectedVoice", voiceId, true); // true = shouldValidate
                                        }
                                    }

                                    // In narrative mode, handle per-capsule voice assignment
                                    if (isNarrativeMode) {
                                        // Determine the compatible model for this voice (default to v4 for ElevenLabs)
                                        const compatibleModel = openAi ? "OPENAI" : DEFAULT_ELEVENLABS_MODEL;

                                        // CRITICAL: In narrative mode, ALWAYS require a focused capsule to apply voice
                                        // Never apply to all capsules automatically - user must explicitly focus a capsule
                                        if (focusedCapsuleId && focusedCapsuleId.trim() !== "") {
                                            // Verify the focused capsule exists
                                            const focusedCapsuleExists = capsules.some(
                                                (cap) => cap.id === focusedCapsuleId,
                                            );
                                            if (focusedCapsuleExists && onCapsuleVoiceChange) {
                                                // Apply to focused capsule ONLY - do not touch any other capsules
                                                onCapsuleVoiceChange(focusedCapsuleId, voiceId);
                                                // Also update the capsule's model to match the voice provider
                                                if (onCapsuleModelChange) {
                                                    onCapsuleModelChange(focusedCapsuleId, compatibleModel);
                                                }
                                            }
                                            // If focusedCapsuleId is set but capsule doesn't exist, do nothing
                                        } else {
                                            // No capsule focused in narrative mode: only update form-level voice
                                            // Do NOT apply to all capsules - user must focus a capsule first
                                            // This prevents accidental voice application to all capsules
                                        }
                                    }
                                } else {
                                    // Voice not found in options
                                    // In narrative mode, check if we should update form-level voice
                                    // Always update form-level voice when a capsule is focused in narrative mode
                                    const isNarrativeMode = values.podcastType === "narrative";
                                    const shouldUpdateFormVoice = !isNarrativeMode || focusedCapsuleId;

                                    if (shouldUpdateFormVoice) {
                                        setFieldValue("selectedVoice", voiceId, true); // true = shouldValidate
                                    }

                                    // In narrative mode, handle per-capsule voice assignment
                                    if (isNarrativeMode) {
                                        // Determine the compatible model for this voice (need to find voice info again)
                                        const pickedVoice = voiceOptions.find((v) => v.value === voiceId);
                                        if (pickedVoice) {
                                            const voiceOpenAi = pickedVoice.origin === "openai";
                                            // Default to v4 for ElevenLabs voices
                                            const compatibleModel = voiceOpenAi ? "OPENAI" : DEFAULT_ELEVENLABS_MODEL;

                                            // CRITICAL: In narrative mode, ALWAYS require a focused capsule to apply voice
                                            // Never apply to all capsules automatically - user must explicitly focus a capsule
                                            if (focusedCapsuleId && focusedCapsuleId.trim() !== "") {
                                                // Verify the focused capsule exists
                                                const focusedCapsuleExists = capsules.some(
                                                    (cap) => cap.id === focusedCapsuleId,
                                                );
                                                if (focusedCapsuleExists && onCapsuleVoiceChange) {
                                                    // Apply to focused capsule ONLY - do not touch any other capsules
                                                    onCapsuleVoiceChange(focusedCapsuleId, voiceId);
                                                    // Also update the capsule's model to match the voice provider
                                                    if (onCapsuleModelChange) {
                                                        onCapsuleModelChange(focusedCapsuleId, compatibleModel);
                                                    }
                                                }
                                                // If focusedCapsuleId is set but capsule doesn't exist, do nothing
                                            } else {
                                                // No capsule focused in narrative mode: only update form-level voice
                                                // Do NOT apply to all capsules - user must focus a capsule first
                                                // This prevents accidental voice application to all capsules
                                            }
                                        }
                                    }
                                }
                            }}
                            loading={false}
                            error={false}
                            label=""
                        />
                    </FormGroup>

                    {/* Audio Model - Only show for admin users and not in conversation mode */}
                    <FormGroup
                        name="audioModel"
                        title={t("model")}
                        style={{ marginTop: 16, maxWidth: "100%" }}
                        size="small"
                    >
                        <Field name="audioModel">
                            {({ field, form, meta }: any) => {
                                const handleModelChange: (value?: string) => void = (newModel?: string) => {
                                    if (!newModel) return;

                                    // In narrative mode, check if we should update form-level model
                                    // Only update form-level if no capsule is focused OR if this is the first model selection
                                    const hasAnyCapsuleModel =
                                        isNarrativeMode &&
                                        capsules.some((cap) => cap.audioModel && cap.audioModel.trim() !== "");
                                    const shouldUpdateFormModel =
                                        !isNarrativeMode || !focusedCapsuleId || !hasAnyCapsuleModel;

                                    if (shouldUpdateFormModel) {
                                        setFieldValue("audioModel", newModel, true);
                                    }

                                    // In narrative mode, handle per-capsule model assignment
                                    if (isNarrativeMode) {
                                        if (focusedCapsuleId && onCapsuleModelChange) {
                                            // Apply to focused capsule only
                                            onCapsuleModelChange(focusedCapsuleId, newModel);
                                        } else {
                                            // No capsule focused: check if any capsule already has an individual model set
                                            const hasIndividualModels = capsules.some(
                                                (cap) => cap.audioModel && cap.audioModel.trim() !== "",
                                            );

                                            if (!hasIndividualModels) {
                                                // First model selection: apply to all capsules
                                                capsules.forEach((cap) => {
                                                    if (onCapsuleModelChange) {
                                                        onCapsuleModelChange(cap.id, newModel);
                                                    }
                                                });
                                            }
                                            // If capsules have individual models and no capsule is focused,
                                            // only update form-level (capsules can be edited by focusing them)
                                        }
                                    }
                                };

                                return (
                                    <DropdownField
                                        {...({
                                            field: {
                                                ...field,
                                                value: currentModel || "",
                                            },
                                            form,
                                            meta,
                                            options: getAvailableModelOptions,
                                            placeholder: t("selectAModel"),
                                            isWithoutMargin: true,
                                            disabled: !currentVoiceId || values.dialogLines.length > 1,
                                            onChange: handleModelChange,
                                        } as any)}
                                    />
                                );
                            }}
                        </Field>
                        {values.dialogLines.length > 1 && (
                            <div
                                style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    marginTop: "8px",
                                    fontStyle: "italic",
                                }}
                            >
                                {t("deleteTextsToChangeModel") || "Supprimez les textes pour modifier le modèle"}
                            </div>
                        )}
                    </FormGroup>
                </div>
            )}

            {/* Speed Slider */}
            {shouldShowSpeedSlider && (
                <FormGroup
                    name="audioSpeed"
                    title={
                        focusedCapsule && focusedCapsuleId
                            ? `${t("speed")} (${t("capsule") || "Capsule"} ${capsules.findIndex((c) => c.id === focusedCapsuleId) + 1})`
                            : t("speed")
                    }
                    style={{ marginTop: 28 }}
                    size="small"
                >
                    <SpeedSlider
                        initialValue={currentSpeed}
                        min={selectedVoiceInfo.origin === "elevenlabs" ? 0.8 : 0.96}
                        max={selectedVoiceInfo.origin === "elevenlabs" ? 1.2 : 1.04}
                        step={0.01}
                        onChange={(newSpeed: number) => {
                            // In narrative mode, check if we should update form-level speed
                            // Only update form-level if no capsule is focused OR if this is the first speed selection
                            // A capsule has an individual speed if it was explicitly set (we can't perfectly detect this,
                            // but we check if any capsule has a speed different from the current form-level speed)
                            const hasAnyCapsuleSpeed =
                                isNarrativeMode &&
                                capsules.some((cap) => {
                                    // If capsule has a speed set and it's different from form-level, it's individual
                                    return (
                                        cap.audioSpeed !== undefined &&
                                        cap.audioSpeed !== null &&
                                        cap.audioSpeed !== audioSpeed
                                    );
                                });
                            const shouldUpdateFormSpeed = !isNarrativeMode || !focusedCapsuleId || !hasAnyCapsuleSpeed;

                            if (shouldUpdateFormSpeed) {
                                // Update form-level speed
                                onAudioSpeedChange(newSpeed);
                            }

                            // In narrative mode, handle per-capsule speed assignment
                            if (isNarrativeMode) {
                                if (focusedCapsuleId && onCapsuleSpeedChange) {
                                    // Apply to focused capsule only
                                    onCapsuleSpeedChange(focusedCapsuleId, newSpeed);
                                } else {
                                    // No capsule focused: check if this is the first speed selection (no capsules have individual speeds yet)
                                    const hasAnySpeedSet = capsules.some((cap) => {
                                        // Check if capsule has a speed different from form-level
                                        return (
                                            cap.audioSpeed !== undefined &&
                                            cap.audioSpeed !== null &&
                                            cap.audioSpeed !== audioSpeed
                                        );
                                    });

                                    if (!hasAnySpeedSet) {
                                        // First speed selection: apply to all capsules
                                        capsules.forEach((cap) => {
                                            if (onCapsuleSpeedChange) {
                                                onCapsuleSpeedChange(cap.id, newSpeed);
                                            }
                                        });
                                    }
                                    // If any capsule already has a speed set and no capsule is focused,
                                    // only update form-level (individual capsules must be focused to edit)
                                }
                            }
                        }}
                    />
                </FormGroup>
            )}

            {/* Add Voice Section - Only show in Conversation mode with a dialogue model (v3, v4) */}
            {isDialogueModel && values.podcastType === "conversation" && (
                <>
                    {/* Audio Model - dialogue models only (v3, v4), applies to the whole conversation */}
                    <FormGroup name="audioModel" title={t("model")} style={{ marginTop: 28 }} size="small">
                        <Dropdown
                            options={getAvailableModelOptions}
                            placeholder={t("selectAModel")}
                            isWithoutMargin={true}
                            value={values.audioModel || ""}
                            onChange={(model: string) => {
                                if (model) setFieldValue("audioModel", model, true);
                            }}
                            loading={false}
                            error={false}
                            label=""
                        />
                    </FormGroup>

                    <h3 className="dropdown-category-title-font" style={{ marginTop: 30, marginBottom: 12 }}>
                        {t("addVoice") || "Ajoutez une voix"}
                    </h3>
                    <FormGroup name="newVoice" title="" style={{ marginTop: 0 }} size="small">
                        <Dropdown
                            options={filteredVoiceOptions.filter((voice) => !selectedVoices.includes(voice.value))}
                            placeholder={t("selectAVoice") || "Sélectionnez voix"}
                            isWithoutMargin={true}
                            value={newVoiceToAdd}
                            onChange={(voiceId: string) => {
                                setNewVoiceToAdd(voiceId);
                                if (voiceId && onAddVoice) {
                                    onAddVoice(voiceId);
                                    setNewVoiceToAdd("");
                                }
                            }}
                            loading={false}
                            error={false}
                            label=""
                        />
                    </FormGroup>

                    {/* Selected Voices List */}
                    {selectedVoices.length > 0 && (
                        <>
                            <h3 className="dropdown-category-title-font" style={{ marginTop: 30, marginBottom: 12 }}>
                                {t("selectedVoices") || "Voix sélectionnées"}
                            </h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                {selectedVoices.map((voiceId) => {
                                    const voiceInfo = voiceOptions.find((v) => v.value === voiceId);
                                    if (!voiceInfo) return null;
                                    const voiceName = voiceInfo.name || voiceInfo.label || t("voice");
                                    const maxNameLength = 20; // Maximum characters before truncation
                                    const displayName =
                                        voiceName.length > maxNameLength
                                            ? `${voiceName.substring(0, maxNameLength)}...`
                                            : voiceName;
                                    const isLastApplied = voiceId === lastAppliedVoice;

                                    return (
                                        <div
                                            key={voiceId}
                                            className={`${styles.voiceChip}${isLastApplied ? ` ${styles.voiceChipAssigned}` : ""}`}
                                        >
                                            {/* Remove Button (X icon) and Voice Name */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                {/* Remove Voice Button */}
                                                <button
                                                    type="button"
                                                    className={`danger-icon-btn ${styles.voiceRemove}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onRemoveVoice) {
                                                            onRemoveVoice(voiceId);
                                                        }
                                                    }}
                                                    title={t("removeVoice") || "Retirer la voix"}
                                                >
                                                    <Icon name="ic-x" />
                                                </button>

                                                {/* Voice Name (truncated) */}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <span className={styles.voiceName} title={voiceName}>
                                                        {displayName}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Apply Button */}
                                            <button
                                                type="button"
                                                className={`${styles.voiceApply}${hasTextSelection ? ` ${styles.voiceApplyReady}` : ""}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (onAssignVoiceToSelection) {
                                                        onAssignVoiceToSelection(voiceId);
                                                    }
                                                }}
                                                disabled={!onAssignVoiceToSelection || !hasTextSelection}
                                                title={
                                                    hasTextSelection
                                                        ? t("applyVoiceToSelection") ||
                                                          "Appliquer la voix à la sélection"
                                                        : t("selectTextToApplyVoice") ||
                                                          "Sélectionnez du texte pour appliquer la voix"
                                                }
                                            >
                                                {t("apply") || "Appliquer"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

interface MainEditorSectionProps {
    t: (key: string, options?: any) => string;
    isDialogueModel: boolean;
    values: FormValues;
    filteredVoiceOptions: any[];
    selectedVoices: string[];
    capsules: Capsule[];
    onCapsuleTitleChange: (capsuleId: string, newSuffix: string, currentPrefix: string) => void;
    onCapsuleRemove: (capsuleId: string) => void;
    getCapsuleTextChangeHandler: (capsuleId: string) => (text: string) => void;
    getCapsuleSegmentsChangeHandler: (capsuleId: string) => (segments: TextSegment[]) => void;
    getCapsuleSelectionHandler: (capsuleId: string) => (hasSelection: boolean) => void;
    getCapsuleAssignVoiceHandler: (capsuleId: string) => (handler: (voiceId: string) => void) => void;
    onAddCapsule: (values: FormValues) => void;
    onCapsuleFocus: (capsuleId: string) => void;
    focusedCapsuleId?: string | null;
    onGlobalVoiceApply: (voiceId: string) => void;
    lastAppliedVoice: string | null;
    buttons?: React.ReactNode;
    selectedAudioId: string | null;
    onAudioSelect: (audioId: string) => void;
    routeId?: string;
}

// Auto-resizing TextField wrapper component
const MainEditorSection: React.FC<MainEditorSectionProps> = ({
    t,
    isDialogueModel,
    values,
    filteredVoiceOptions,
    selectedVoices,
    capsules,
    onCapsuleTitleChange,
    onCapsuleRemove,
    getCapsuleTextChangeHandler,
    getCapsuleSegmentsChangeHandler,
    getCapsuleSelectionHandler,
    getCapsuleAssignVoiceHandler,
    onAddCapsule,
    onCapsuleFocus,
    focusedCapsuleId,
    onGlobalVoiceApply,
    lastAppliedVoice,
    buttons,
    selectedAudioId,
    onAudioSelect,
    routeId,
}) => {
    return (
        <div className={styles.leftSection}>
            <div className={styles.leftSectionScroll}>
                {/* Map over ALL capsules - no special first one */}
                {capsules.map((capsule, index) => {
                    const capsuleId = capsule.id;
                    const capsuleTextChangeHandler = getCapsuleTextChangeHandler(capsuleId);
                    const capsuleSegmentsChangeHandler = getCapsuleSegmentsChangeHandler(capsuleId);
                    const capsuleSelectionHandler = getCapsuleSelectionHandler(capsuleId);
                    const capsuleAssignVoiceHandler = getCapsuleAssignVoiceHandler(capsuleId);
                    // Calculate margin-left to align with text area
                    // In conversation mode: badge container is present (56px) + text wrapper padding (6px)
                    // In narrative mode: no badge container, text starts at padding (20px)
                    const isConversationMode = isDialogueModel && values.podcastType === "conversation";
                    const BADGE_CONTAINER_WIDTH = 56; // Fixed width for conversation mode
                    const textAreaStart = isConversationMode
                        ? 20 + BADGE_CONTAINER_WIDTH + 6 // Conversation: padding + badge + text wrapper padding
                        : 20; // Narrative: just padding

                    // Get the prefix from values.textObject (form title)
                    const currentPrefix = values.textObject || "";
                    const fullTitle = capsule.textObject || `${t("capsule") || "Capsule"} ${index + 1}`;

                    // Extract suffix (everything after prefix)
                    let suffix = "";
                    if (currentPrefix && fullTitle.startsWith(currentPrefix)) {
                        // Title starts with prefix, extract everything after it
                        suffix = fullTitle.substring(currentPrefix.length);
                    } else if (!currentPrefix) {
                        // If no prefix set yet, allow editing the whole title
                        suffix = fullTitle;
                    } else {
                        // Title doesn't match prefix structure - try to extract numbering part
                        // First, check if it's a default "Capsule X" format (works with any language)
                        const capsuleLabel = t("capsule") || "Capsule";
                        const defaultCapsulePattern = new RegExp(`^${capsuleLabel}\\s+(\\d+)$`, "i");
                        const defaultCapsuleMatch = fullTitle.match(defaultCapsulePattern);
                        if (defaultCapsuleMatch) {
                            // Extract just the numbering part (e.g., " - 1")
                            suffix = ` - ${defaultCapsuleMatch[1]}`;
                        } else {
                            // Look for pattern like " - 1", " - 2", etc. at the end
                            const numberingMatch = fullTitle.match(/\s*-\s*(\d+)$/);
                            if (numberingMatch) {
                                // Extract just the numbering part (e.g., " - 1")
                                suffix = numberingMatch[0];
                            } else {
                                // No numbering found, use full title as suffix
                                suffix = fullTitle;
                            }
                        }
                    }

                    // Determine if suffix has content (not empty after trimming)
                    const hasContent = suffix && suffix.trim().length > 0;
                    // Smaller padding when empty, bigger when has content
                    const paddingRight = hasContent ? "20px" : "5px";
                    const isFocused = focusedCapsuleId === capsuleId;

                    return (
                        <div key={capsuleId} className={styles.capsuleBlock}>
                            {index > 0 && <div className={styles.capsuleDivider} aria-hidden />}
                            <div
                                className={styles.capsuleInner}
                                style={{
                                    marginTop: index > 0 ? "20px" : "0px",
                                    position: "relative",
                                    paddingTop: "20px",
                                    paddingRight: "36px",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <div
                                    style={{
                                        marginLeft: `${textAreaStart}px`,
                                        marginBottom: "10px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <div
                                        className={`${styles.capPill}${isFocused ? ` ${styles.capPillActive}` : ""}`}
                                        style={{
                                            paddingRight: paddingRight === "20px" ? "14px" : "8px",
                                        }}
                                    >
                                        <Icon name="ic-audio-lines" />
                                        {/* Read-only prefix */}
                                        {currentPrefix && <span className={styles.capPillPrefix}>{currentPrefix}</span>}
                                        {/* Editable suffix - using contentEditable div for proper width sizing */}
                                        <div
                                            key={`capsule-title-${capsuleId}-${routeId || "new"}`}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onBlur={(e) => {
                                                const newValue = e.currentTarget.textContent || "";
                                                onCapsuleTitleChange(capsuleId, newValue, currentPrefix);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    e.currentTarget.blur();
                                                }
                                            }}
                                            onInput={(e) => {
                                                // Update placeholder visibility
                                                const div = e.currentTarget;
                                                const hasContent = div.textContent
                                                    ? div.textContent?.trim().length > 0
                                                    : false;
                                                if (hasContent) {
                                                    div.classList.remove("empty");
                                                } else {
                                                    div.classList.add("empty");
                                                }
                                            }}
                                            className={`${styles.capPillTitle}${!suffix || suffix.trim() === "" ? " empty" : ""}`}
                                            style={{
                                                minWidth: currentPrefix ? "20px" : "50px",
                                                textAlign: currentPrefix ? "left" : "center",
                                            }}
                                            data-placeholder={
                                                currentPrefix ? "" : `${t("capsule") || "Capsule"} ${index + 1}`
                                            }
                                        >
                                            {suffix}
                                        </div>
                                    </div>
                                    {/* Play icon if audio is available - inline with badge */}
                                    {capsule.audioUrl && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const audioId = capsule.audioId || capsule.id;
                                                onAudioSelect(audioId);
                                            }}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                padding: 0,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                flexShrink: 0,
                                            }}
                                            title={t("audioAvailable") || "Audio available"}
                                        >
                                            <img
                                                src={playIcon}
                                                alt="Play"
                                                style={{
                                                    width: "20px",
                                                    height: "20px",
                                                    filter:
                                                        (capsule.audioId || capsule.id) === selectedAudioId
                                                            ? "brightness(0) saturate(100%) invert(70%) sepia(100%) saturate(2000%) hue-rotate(160deg) brightness(1.1) contrast(1.2)"
                                                            : "brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(130deg) brightness(95%) contrast(85%)",
                                                    transition: "filter 0.2s ease",
                                                }}
                                            />
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className={`danger-icon-btn ${styles.capsuleRemove}`}
                                    onClick={() => onCapsuleRemove(capsuleId)}
                                    title={t("removeCapsule") || "Supprimer la capsule"}
                                >
                                    <Icon name="ic-x" />
                                </button>
                                {isDialogueModel && values.podcastType === "conversation" ? (
                                    <div key={`capsule-wrapper-${capsuleId}`} onClick={() => onCapsuleFocus(capsuleId)}>
                                        <div
                                            className={styles.contentContainerTextarea}
                                            style={{ position: "relative", padding: "10px 20px", borderBottom: "none" }}
                                        >
                                            <TextWithVoiceSelection
                                                key={`text-voice-${capsuleId}`}
                                                fullText={capsule.textValue || ""}
                                                onTextChange={capsuleTextChangeHandler}
                                                segments={capsule.textSegments}
                                                onSegmentsChange={capsuleSegmentsChangeHandler}
                                                voiceOptions={
                                                    selectedVoices.length > 0
                                                        ? filteredVoiceOptions.filter((voice) =>
                                                              selectedVoices.includes(voice.value),
                                                          )
                                                        : filteredVoiceOptions
                                                }
                                                placeholder={t("typeYourTextHere")}
                                                onSelectionChange={capsuleSelectionHandler}
                                                assignVoiceToSelection={capsuleAssignVoiceHandler}
                                                autoFillWithLastVoice={capsule.useMultipleNarrators || isDialogueModel}
                                                onGlobalVoiceApply={onGlobalVoiceApply}
                                                lastAppliedVoice={lastAppliedVoice || undefined}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div key={`capsule-wrapper-${capsuleId}`} onClick={() => onCapsuleFocus(capsuleId)}>
                                        <div
                                            className={styles.contentContainerTextarea}
                                            style={{ position: "relative", padding: "10px 20px", borderBottom: "none" }}
                                        >
                                            <Field name={`capsuleText-${capsuleId}`}>
                                                {({ field, form, meta }: any) => (
                                                    <AutoResizeTextField
                                                        field={{
                                                            ...field,
                                                            value: capsule.textValue || "",
                                                        }}
                                                        form={form}
                                                        meta={meta}
                                                        placeholder={t("typeYourTextHere")}
                                                        size="big"
                                                        numberOfLines={5}
                                                        style={{
                                                            padding: "10px",
                                                            border: "none",
                                                            borderColor: "transparent",
                                                            boxShadow: "none",
                                                            background: "transparent",
                                                        }}
                                                        value={capsule.textValue || ""}
                                                        onChange={(
                                                            e: React.ChangeEvent<
                                                                HTMLInputElement | HTMLTextAreaElement
                                                            >,
                                                        ) => {
                                                            const newValue = e.target.value;
                                                            capsuleTextChangeHandler(newValue);
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* Add Capsule Button */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "24px",
                        position: "relative",
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            height: "1px",
                            backgroundColor: "#e0e0e0",
                            marginRight: "12px",
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => onAddCapsule(values)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 16px",
                            backgroundColor: "transparent",
                            color: "#213137",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: 500,
                        }}
                    >
                        <div
                            style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: "#f5f5f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "20px",
                                color: "#213137",
                            }}
                        >
                            +
                        </div>
                        <span>{t("addCapsule") || "Ajouter une capsule"}</span>
                    </button>
                    <div
                        style={{
                            flex: 1,
                            height: "1px",
                            backgroundColor: "#e0e0e0",
                            marginLeft: "12px",
                        }}
                    />
                </div>
            </div>
            {buttons}
        </div>
    );
};

interface AudioOption {
    id: string;
    title: string;
    url: string | null;
}

interface GenerateActionsSectionProps {
    t: (key: string, options?: any) => string;
    wordCount: number;
    charCount: number;
    charLimit?: number;
    isDisabled: boolean;
    tooltipMessage: string;
    loadingTTSGeneration: boolean;
    submitForm: () => void;
    waitingRank: number | null;
    audioUrl: string | null;
    audioTitle: string;
    generationStatus: "waiting" | "success" | "error";
    onNavigateToTranslation: () => void;
    onOpenProjectModal: () => void;
    isCharLimitExceeded: boolean;
    audioOptions?: AudioOption[];
    selectedAudioId?: string | null;
    onAudioSelect?: (audioId: string) => void;
    focusedCapsuleId?: string | null;
    capsules?: Capsule[];
    capsuleProjectInfo?: Record<string, { isInProject: boolean; projectName?: string }>;
}

const GenerateActionsSection: React.FC<GenerateActionsSectionProps> = ({
    t,
    wordCount,
    charCount,
    charLimit,
    isDisabled,
    tooltipMessage,
    loadingTTSGeneration,
    submitForm,
    waitingRank,
    audioUrl,
    audioTitle,
    generationStatus,
    onNavigateToTranslation,
    onOpenProjectModal,
    isCharLimitExceeded,
    audioOptions = [],
    selectedAudioId,
    onAudioSelect,
    focusedCapsuleId,
    capsules = [],
    capsuleProjectInfo = {},
}) => {
    const renderGenerateButton = (label: string) => {
        const button = (
            <Button
                label={label}
                variant="primary"
                shape="default"
                className={styles.generateBtn}
                onClick={submitForm}
                disabled={isDisabled}
            />
        );
        const content = tooltipMessage ? (
            <Tooltip title={tooltipMessage} placement="top">
                <div>{button}</div>
            </Tooltip>
        ) : (
            button
        );
        return <div className={styles.generateSlot}>{content}</div>;
    };

    // Show add to project button if any audio option has a URL and at least one is not in a project
    const hasAnyAudioUrl = audioOptions.some((opt) => opt.url);
    const hasUnaddedVariants = audioOptions.some((opt) => {
        const capsule = capsules.find((c) => (c.audioId || c.id) === opt.id);
        if (!capsule) return false;
        const capsuleId = capsule.audioId || capsule.id;
        const projectInfo = capsuleProjectInfo[capsuleId];
        // If no project info, assume not in project (for newly generated audio)
        return !projectInfo || !projectInfo.isInProject;
    });

    const renderAddToProjectButton = () =>
        hasAnyAudioUrl && hasUnaddedVariants ? <AddToProjectButton onClick={onOpenProjectModal} /> : null;

    console.log("[TextToSpeechForm] Render state:", {
        audioUrl,
        audioTitle,
        selectedAudioId,
        audioOptionsCount: audioOptions.length,
        hasAnyAudioUrl,
        generationStatus,
    });

    const generateLabel =
        waitingRank !== null && waitingRank > 0
            ? t("waitingInQueue", { rank: waitingRank })
            : loadingTTSGeneration
              ? t("generating")
              : t("generateSpeech");

    return (
        <>
            <div className={styles.ctaBar}>
                <div className={styles.ctaButtons}>
                    {!charLimit && (
                        <p className={styles.wordCount}>
                            {t("wordCountSemicolon")} {wordCount}
                        </p>
                    )}
                    {Boolean(charLimit) && (
                        <p
                            className={styles.wordCount}
                            style={{
                                color: isCharLimitExceeded ? "#ff4d4d" : "#666",
                            }}
                        >
                            {focusedCapsuleId
                                ? `${t("capsule") || "Capsule"} ${capsules.findIndex((c) => c.id === focusedCapsuleId) + 1}: `
                                : ""}
                            {t("characterCountSemicolon") || "Characters:"} {charCount}/{charLimit}
                        </p>
                    )}
                    <div className={styles.footActions}>
                        {renderGenerateButton(generateLabel)}
                        {generationStatus === "success" && audioUrl && (
                            <Button
                                label={t("nextStep")}
                                variant="outline"
                                shape="pill"
                                className={styles.ctaBtn}
                                onClick={onNavigateToTranslation}
                            />
                        )}
                        {renderAddToProjectButton()}
                    </div>
                </div>
            </div>
            {audioUrl ? (
                <div className={styles.ttsPlayerFooter}>
                    <MultiAudioPlayer
                        audioOptions={audioOptions}
                        selectedAudioId={selectedAudioId ?? null}
                        onAudioSelect={(audioId: string) => {
                            if (onAudioSelect) {
                                onAudioSelect(audioId);
                            }
                        }}
                        onClickAddToProject={onOpenProjectModal}
                    />
                </div>
            ) : null}
        </>
    );
};

// Component to sync Formik's textObject value to parent component state
// This avoids the "Cannot update component while rendering" error
const TextObjectSync: React.FC<{ onTextObjectChange: (textObject: string) => void }> = ({ onTextObjectChange }) => {
    const { values } = useFormikContext<FormValues>();

    useEffect(() => {
        onTextObjectChange(values.textObject || "");
    }, [values.textObject, onTextObjectChange]);

    return null;
};

// Component to clear stale voice errors when switching to narrative mode
// This prevents Yup schema errors from persisting when capsules have voices
const VoiceErrorCleanup: React.FC = () => {
    const { values, setFieldValue } = useFormikContext<FormValues>();

    useEffect(() => {
        if (values.podcastType === "narrative") {
            // Remove any leftover required error the user might have seen earlier
            // Setting the field value (even to the same value) with shouldValidate=false clears stale errors
            setFieldValue("selectedVoice", values.selectedVoice || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.podcastType]);

    return null;
};

// Extract the "suffix" part of a capsule title given the old prefix and index.
// Handles cases like "My Title - 1", "Capsule 2", or any free text.
// Return both the suffix and whether we actually matched an old prefix.
function splitTitle(
    fullTitle: string,
    oldPrefix: string,
    index: number,
    capsuleLabel: string, // e.g. t("capsule") || "Capsule"
): { suffix: string; hadPrefix: boolean } {
    const title = fullTitle ?? "";

    // 1) If title starts with the old prefix, take the remainder as suffix
    if (oldPrefix && title.startsWith(oldPrefix)) {
        const remainder = title.slice(oldPrefix.length);
        return { suffix: remainder, hadPrefix: true };
    }

    // 2) Detect "Capsule X" (localized) and turn it into " - X"
    //    This covers initial default titles before a prefix existed.
    const defaultCapsulePattern = new RegExp(`^${capsuleLabel}\\s+(\\d+)$`, "i");
    const m = title.match(defaultCapsulePattern);
    if (m) {
        return { suffix: ` - ${m[1]}`, hadPrefix: false };
    }

    // 3) Detect " - X" at the end and keep it as suffix
    const numberingMatch = title.match(/\s*-\s*(\d+)$/);
    if (numberingMatch) {
        return { suffix: numberingMatch[0], hadPrefix: false };
    }

    // 4) Otherwise, keep the entire title as suffix (free text case)
    return { suffix: title, hadPrefix: false };
}

const TextToSpeech: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    // Texts imported from a project page via multi-select. Consumed once on mount.
    const importedCapsulesRef = useRef<{ name: string; text: string; language: string | null }[] | null>(
        Array.isArray((location.state as any)?.importedCapsules)
            ? ((location.state as any).importedCapsules as {
                  name: string;
                  text: string;
                  language: string | null;
              }[])
            : null,
    );
    const importedProjectNameRef = useRef<string>(
        typeof (location.state as any)?.projectName === "string" ? ((location.state as any).projectName as string) : "",
    );
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const { showNotification } = useNotification();
    const [voiceOptions, setVoiceOptions] = useState<any[]>([]);
    const { resolveVoiceIdForApi, resolveVoiceIdFromClone, resolveVoiceIdFromPrompt } = useVoiceResolvers(voiceOptions);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [audioTitle, setAudioTitle] = useState<string>("");
    const [selectedAudioId, setSelectedAudioId] = useState<string | null>(null);
    const [loadingTTSGeneration, setLoadingTTSGeneration] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isVariantsModalOpen, setIsVariantsModalOpen] = useState<boolean>(false);
    const [capsuleProjectInfo, setCapsuleProjectInfo] = useState<
        Record<string, { isInProject: boolean; projectName?: string }>
    >({});
    const [audioSpeed, setAudioSpeed] = useState<number>(1.0);
    const [languageCode, setLanguageCode] = useState<string | null>("");
    const [waitingRank, setWaitingRank] = useState<number | null>(null);
    const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
    const [generationStatus, setGenerationStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [errorReason, setErrorReason] = useState<"generic" | "quota" | "statusUnavailable">("generic");
    const { isAudioExhausted, refetch: refetchTokens, data: tokenData } = useRemainingTokens();
    const { cancelling, cancelGeneration, confirmCanceled, resetCancelling } = useCancelGeneration();
    /**
     * Parent of the batch being generated: canceling it cancels all its variants. Only set once
     * every capsule is queued, so the cancel button never shows while some are still being sent.
     */
    const [generatingParentId, setGeneratingParentId] = useState<string | null>(null);
    /**
     * Bumped when a canceled batch is discarded: its AudioAIs are deleted, so every polling loop
     * started before must stop instead of reading them (it would report an error, or retry forever).
     */
    const pollGenerationRef = useRef<number>(0);
    /** Set while this page's own cancel request runs: its response concludes the batch, not the polling. */
    const cancelRequestedRef = useRef<boolean>(false);
    const remainingWords = tokenData?.remainigAudioTokens;
    const checkStatusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isCheckingStatusRef = useRef<boolean>(false);
    /** Consecutive failed status polls during bulk generation (reset on success). */
    const statusPollFailuresRef = useRef<number>(0);
    /** Parent to open when user closes the "status unavailable" modal (avoid auto-navigate during outage). */
    const statusUnavailableParentIdRef = useRef<string | null>(null);
    const [selectedVoices, setSelectedVoices] = useState<string[]>([]);
    const [hasTextSelection, setHasTextSelection] = useState<boolean>(false);
    const [lastAppliedVoice, setLastAppliedVoice] = useState<string | null>(null);
    const [capsules, setCapsules] = useState<Capsule[]>([]);
    const [activeCapsuleId, setActiveCapsuleId] = useState<string | null>(null);
    // State to track textObject value for syncing capsule titles (updated from Formik render)
    const [currentTextObject, setCurrentTextObject] = useState<string>("");
    const [focusedCapsuleId, setFocusedCapsuleId] = useState<string | null>(null);
    // Bootstrap data for initial form values
    const [audioBootstrap, setAudioBootstrap] = useState<null | {
        name?: string;
        language?: string; // could be code or ID
        model?: string;
        selectedVoice?: string;
        selectedSecondVoice?: string;
        audioSpeed?: number;
        selectedTextType?: "isText" | "isdialog";
        podcastType?: "narrative" | "conversation";
    }>(null);
    const [capsuleAssignVoiceHandlers, setCapsuleAssignVoiceHandlers] = useState<
        Record<string, (voiceId: string) => void>
    >({});
    const capsuleSelectionHandlersRef = useRef<Record<string, (hasSelection: boolean) => void>>({});
    const capsuleAssignVoiceHandlersRef = useRef<Record<string, (handler: (voiceId: string) => void) => void>>({});
    const capsuleSegmentsChangeHandlersRef = useRef<Record<string, (segments: TextSegment[]) => void>>({});
    const capsuleTextChangeHandlersRef = useRef<Record<string, (text: string) => void>>({});
    const resetCapsuleState = useCallback(() => {
        capsuleSelectionHandlersRef.current = {};
        capsuleAssignVoiceHandlersRef.current = {};
        capsuleSegmentsChangeHandlersRef.current = {};
        capsuleTextChangeHandlersRef.current = {};
        setCapsuleAssignVoiceHandlers({});
    }, [setCapsuleAssignVoiceHandlers]);
    const setCapsulesSafely = useCallback(
        (capsuleList: Capsule[]) => {
            resetCapsuleState();
            setCapsules(capsuleList);
        },
        [resetCapsuleState, setCapsules],
    );
    useEffect(() => {
        resetCapsuleState();
        loadingAudioIdRef.current = null; // Reset loading flag when ID changes
        lastPrefixRef.current = ""; // Reset prefix ref when ID changes

        // If no ID in URL, initialize with one empty capsule (or with capsules imported from a project)
        if (!id) {
            const imported = importedCapsulesRef.current;
            const baseTs = Date.now();
            const seeds =
                imported && imported.length > 0
                    ? imported.map((c, idx) => ({
                          id: `capsule-${baseTs}-${idx + 1}`,
                          textValue: c.text || "",
                          textObject: c.name || "",
                          selectedContentLanguage: "",
                          selectedVoice: "",
                          selectedSecondVoice: "",
                          selectedTextType: "isText" as const,
                          audioSpeed: 1.0,
                          audioModel: "",
                          dialogLines: [],
                          useMultipleNarrators: false,
                          textSegments: [],
                          audioUrl: null,
                      }))
                    : [
                          {
                              id: `capsule-${baseTs}-1`,
                              textValue: "",
                              textObject: "",
                              selectedContentLanguage: "",
                              selectedVoice: "",
                              selectedSecondVoice: "",
                              selectedTextType: "isText" as const,
                              audioSpeed: 1.0,
                              audioModel: "",
                              dialogLines: [],
                              useMultipleNarrators: false,
                              textSegments: [],
                              audioUrl: null,
                          },
                      ];
            setCapsules(seeds);
            // Set the first capsule as focused by default
            setFocusedCapsuleId(seeds[0].id);
        } else {
            // If ID exists, capsules will be loaded from API
            setCapsules([]);
            setFocusedCapsuleId(null);
        }
        setSelectedVoices([]);
        setActiveCapsuleId(null);
        setHasTextSelection(false);
        setLastAppliedVoice(null); // Reset last applied voice when reentering page
        setAudioUrl(null);
        setAudioTitle("");
        setSelectedAudioId(null);
        setAudioSpeed(1.0);
        setCapsuleProjectInfo({});
    }, [id, resetCapsuleState]);

    // Bootstrap audio data for initial form values (only when editing existing audio)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!id) {
                setAudioBootstrap(null);
                return;
            }
            const token = localStorage.getItem("token");
            if (!token) {
                setAudioBootstrap(null);
                return;
            }
            try {
                const res = await getWaitingAudioAI({ token, id });
                if (cancelled) return;
                if (res?.audioAI) {
                    const audio = res.audioAI;
                    const queue = res.queueAI;
                    // Determine podcast type from dialog lines
                    const normalizedParent = normalizeDialogLines(audio?.dialogLines);
                    const hasDialogLines = normalizedParent.dialogLines.length > 0;
                    const podcastType = hasDialogLines ? "conversation" : "narrative";

                    // Resolve voiceCloneId/voicePromptId to VoiceAI ID for dropdown (only if voiceOptions are loaded)
                    let resolvedVoiceId: string | null | undefined = audio.voiceAIId;
                    if (!resolvedVoiceId && audio.voiceCloneId && voiceOptions.length > 0) {
                        resolvedVoiceId = resolveVoiceIdFromClone(audio.voiceCloneId);
                    }
                    if (!resolvedVoiceId && audio.voicePromptId && voiceOptions.length > 0) {
                        resolvedVoiceId = resolveVoiceIdFromPrompt(audio.voicePromptId);
                    }
                    if (!resolvedVoiceId && queue?.queueAIAudio?.voiceAIId) {
                        resolvedVoiceId = queue.queueAIAudio.voiceAIId;
                    }
                    if (!resolvedVoiceId && queue?.queueAIAudio?.voiceCloneId && voiceOptions.length > 0) {
                        resolvedVoiceId = resolveVoiceIdFromClone(queue.queueAIAudio.voiceCloneId);
                    }
                    if (!resolvedVoiceId && queue?.queueAIAudio?.voicePromptId && voiceOptions.length > 0) {
                        resolvedVoiceId = resolveVoiceIdFromPrompt(queue.queueAIAudio.voicePromptId);
                    }

                    setAudioBootstrap({
                        name: audio.name,
                        language: audio.language,
                        model: audio.model || undefined,
                        selectedVoice: resolvedVoiceId || undefined,
                        selectedSecondVoice: audio.voiceAISecondId || queue?.queueAIAudio?.voiceAISecondId || undefined,
                        audioSpeed: queue?.queueAIAudio?.audioSpeed || 1.0,
                        selectedTextType: audio.isDialog ? "isdialog" : "isText",
                        podcastType,
                    });
                } else {
                    setAudioBootstrap(null);
                }
            } catch (error) {
                console.error("Error loading audio bootstrap:", error);
                if (!cancelled) {
                    setAudioBootstrap(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id, voiceOptions, resolveVoiceIdFromClone, resolveVoiceIdFromPrompt]);

    // Resolve language code or ID to language AI ID
    const resolveLanguageId = useCallback(
        (input?: string | null) => {
            if (!input) return "";
            const languages = data?.languagesAI || [];
            // Accept either code or id
            return languages.find((l: any) => l.value === input || l.code === input)?.value ?? "";
        },
        [data?.languagesAI],
    );

    // Check if bootstrap data is ready
    const languages = data?.languagesAI ?? [];
    const languagesReady = languages.length > 0;
    const bootstrapReady = languagesReady && (!!audioBootstrap || !id); // if creating new: no audioBootstrap needed

    const initialValues: FormValues = {
        textObject: audioBootstrap?.name ?? importedProjectNameRef.current ?? "",
        selectedContentLanguage: resolveLanguageId(audioBootstrap?.language),
        selectedVoice: audioBootstrap?.selectedVoice ?? "",
        selectedSecondVoice: audioBootstrap?.selectedSecondVoice ?? "",
        selectedTextType: audioBootstrap?.selectedTextType ?? "isText",
        audioSpeed: audioBootstrap?.audioSpeed ?? 1.0,
        audioModel: audioBootstrap?.model ?? "",
        dialogLines: [],
        useMultipleNarrators: true,
        podcastType: audioBootstrap?.podcastType ?? "narrative",
    };

    // Load full audio data (capsules, variants, etc.) after bootstrap is ready
    // This runs after the form is initialized with bootstrap data
    useEffect(() => {
        if (!id || !bootstrapReady) return;
        // Load full data including capsules and variants, silent so no modal
        handleAudioAIAwaiting(id, (vals) => setValuesRef.current(vals), { force: true, silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, bootstrapReady]);

    const validationSchema = Yup.object({
        textObject: Yup.string().required(t("titleRequired")),
        selectedContentLanguage: Yup.string().required(t("languageRequired")),
        // Voice validation is handled in runtime logic (hasValidVoice) where we can check capsules
        // This prevents Yup from blocking with stale errors when capsules have voices
        selectedVoice: Yup.string().nullable(),
        selectedSecondVoice: Yup.string().when(["selectedTextType", "useMultipleNarrators"], {
            is: (selectedTextType: string, useMultipleNarrators: boolean) =>
                selectedTextType === "isdialog" && !useMultipleNarrators,
            then: (schema) => schema.required(t("secondVoiceRequired")),
            otherwise: (schema) => schema.notRequired().nullable(),
        }),
        selectedTextType: Yup.mixed<"isText" | "isdialog">()
            .oneOf(["isText", "isdialog"])
            .when("useMultipleNarrators", {
                is: true,
                then: (schema) => schema.notRequired().nullable(),
                otherwise: (schema) => schema.required(t("textTypeRequired")),
            }),
        audioSpeed: Yup.number().min(0.8).max(1.2),
        dialogLines: Yup.array()
            .test("elevenlabs-dialog-character-limit", function (value) {
                if (this.parent.useMultipleNarrators !== true) {
                    return true;
                }
                const maxCharacters = getModelCharLimit(this.parent.audioModel);
                if (!maxCharacters) return true;
                if (!value || !Array.isArray(value) || value.length === 0) return true;
                const totalCharacters = value.reduce((sum, line) => {
                    return sum + (line?.text ? line.text.length : 0);
                }, 0);
                if (totalCharacters <= maxCharacters) return true;
                return this.createError({
                    message:
                        t("elevenlabsV3DialogCharacterLimitExceeded", { max: maxCharacters }) ||
                        `Total character count for dialog lines exceeds the maximum limit of ${maxCharacters} characters.`,
                });
            })
            .when("useMultipleNarrators", {
                is: false,
                then: (schema) => schema.notRequired().nullable(),
                otherwise: (schema) => schema,
            }),
    });

    const fetchVoices = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }
        const voiceAI = await getVoices({ token, language: "EN" });
        if (voiceAI) {
            setVoiceOptions(voiceAI.filter((voice) => voice.isActivated === true));
        } else {
            console.error("Failed to fetch voices");
        }
    };

    useEffect(() => {
        fetchVoices();
    }, []);

    const loadingAudioIdRef = useRef<string | null>(null);
    // Ref to store Formik's setValues function so we can call it from checkAllAudioStatus
    const setValuesRef = useRef<(v: Partial<FormValues>) => void>(() => {});
    // Ref to track current route ID to avoid stale closures
    const routeIdRef = useRef<string | undefined>(id);
    // Ref to track previous textObject prefix for syncing capsule titles
    const lastPrefixRef = useRef<string>("");
    useEffect(() => {
        routeIdRef.current = id;
    }, [id]);

    type AwaitingOpts = { force?: boolean; silent?: boolean };

    const handleAudioAIAwaiting = useCallback(
        async (id: string, setValues: (values: Partial<FormValues>) => void, opts: AwaitingOpts = {}) => {
            const { force = false, silent = false } = opts;

            // Allow forced refetch to bypass the guard
            if (!force && loadingAudioIdRef.current === id) {
                console.log("[TextToSpeech] Already loading audio:", id);
                return;
            }

            loadingAudioIdRef.current = id;

            // Silent mode to avoid flickering the status modal
            if (!silent) {
                setLoadingTTSGeneration(true);
                setShowStatusModal(true);
                setGenerationStatus("waiting");
                setWaitingRank(null);
            }

            const pollGeneration = pollGenerationRef.current;
            const isStale = () => pollGenerationRef.current !== pollGeneration;

            const fetchAudioAI = async () => {
                if (isStale()) return;
                try {
                    const token = localStorage.getItem("token");
                    if (!token) {
                        return;
                    }

                    const audioAI = await getWaitingAudioAI({
                        token: token,
                        id,
                    });
                    if (isStale()) return;
                    if (!audioAI || !audioAI.audioAI) {
                        console.error("Unexpected response format:", audioAI);
                        setLoadingTTSGeneration(false);
                        return;
                    }

                    if (audioAI?.failed) {
                        setLoadingTTSGeneration(false);
                        setGenerationStatus("error");
                        return;
                    }

                    let { waiting, audioAI: audio, queueAI: queue } = audioAI;

                    // If the loaded audio is a variant (has parentId), load the parent instead
                    // to get all variants together
                    if (audio?.parentId) {
                        try {
                            const parentResponse = await getWaitingAudioAI({
                                token,
                                id: audio.parentId,
                            });
                            if (parentResponse?.audioAI) {
                                audio = parentResponse.audioAI;
                                queue = parentResponse.queueAI;
                                waiting = parentResponse.waiting;
                            }
                        } catch (parentError) {
                            console.error("Error loading parent audio", parentError);
                            // Continue with the variant if parent loading fails
                        }
                    }

                    // Normalize URL to parent ID if we resolved a variant to its parent
                    const parentIdResolved = audio?.id;
                    if (parentIdResolved && routeIdRef.current && routeIdRef.current !== parentIdResolved) {
                        navigate(`/create/text-to-speech/${parentIdResolved}`, { replace: true });
                    }

                    const text = audio?.text || "";
                    const variants = Array.isArray(audio?.variants) ? audio.variants : [];

                    console.log("[TextToSpeech] Loading audio:", {
                        audioId: audio?.id,
                        hasVariants: variants.length > 0,
                        variantCount: variants.length,
                        variantIds: variants.map((v: any) => v?.id),
                    });

                    let resolvedVariants = variants;
                    if (variants.length > 0) {
                        resolvedVariants = await Promise.all(
                            variants.map(async (variant) => {
                                if (!variant?.id) {
                                    return variant;
                                }

                                try {
                                    const variantResponse = await getWaitingAudioAI({
                                        token,
                                        id: variant.id,
                                    });

                                    const variantAudio = variantResponse?.audioAI;
                                    if (variantAudio) {
                                        // Track project association for this variant
                                        const isInProject = !!variantAudio.projectAIId;
                                        if (variant.id) {
                                            setCapsuleProjectInfo((prev) => ({
                                                ...prev,
                                                [variant.id]: {
                                                    isInProject,
                                                    projectName: variantAudio.projectAIId ? undefined : undefined, // Could fetch project name if needed
                                                },
                                            }));
                                        }
                                        return {
                                            ...variant,
                                            ...variantAudio,
                                            dialogLines:
                                                (variantAudio.dialogLines && Array.isArray(variantAudio.dialogLines)
                                                    ? variantAudio.dialogLines
                                                    : []) ||
                                                (variant.dialogLines && Array.isArray(variant.dialogLines)
                                                    ? variant.dialogLines
                                                    : []),
                                            audioUrl: variantAudio.audioUrl ?? variant.audioUrl ?? null,
                                            text:
                                                typeof variantAudio.text === "string"
                                                    ? variantAudio.text
                                                    : variant.text,
                                            name: variantAudio.name ?? variant.name,
                                        };
                                    }
                                } catch (variantError) {
                                    console.error("Error fetching variant audio", variantError);
                                }

                                return variant;
                            }),
                        );

                        console.log("[TextToSpeech] Resolved variants:", {
                            count: resolvedVariants.length,
                            variants: resolvedVariants.map((v: any) => ({
                                id: v?.id,
                                name: v?.name,
                                hasAudioUrl: !!v?.audioUrl,
                                audioUrl: v?.audioUrl,
                            })),
                        });
                    }

                    if (isStale()) return;

                    const formValues: Partial<FormValues> = {};
                    let capsulesToSet: Capsule[] = [];

                    // Don't load text into form values - all text goes into capsules now

                    // Determine podcast type from parent data and all variants
                    // Check if any audio (parent or variant) has dialogLines
                    const normalizedParent = normalizeDialogLines(audio?.dialogLines);
                    let hasDialogLines = normalizedParent.dialogLines.length > 0;

                    // Also check all variants for dialogLines if parent doesn't have them
                    if (!hasDialogLines && resolvedVariants.length > 0) {
                        hasDialogLines = resolvedVariants.some((variant: any) => {
                            const normalizedVariant = normalizeDialogLines(variant.dialogLines);
                            return normalizedVariant.dialogLines.length > 0;
                        });
                    }

                    formValues.podcastType = hasDialogLines ? "conversation" : "narrative";

                    // Force the default model when conversation mode is detected (unless already a dialogue model)
                    if (hasDialogLines && !supportsDialogue(formValues.audioModel)) {
                        formValues.audioModel = DEFAULT_ELEVENLABS_MODEL;
                    }

                    if (queue?.queueAIText) {
                        formValues.textObject = queue.queueAIText.textObject;
                        formValues.selectedContentLanguage = queue.queueAIText.languageAIId;
                        if (!formValues.selectedTextType) {
                            formValues.selectedTextType = "isText";
                        }
                    }

                    if (queue?.queueAIAudio) {
                        // Resolve voiceCloneId/voicePromptId to VoiceAI ID for dropdown
                        const primaryQueueVoiceId =
                            queue.queueAIAudio.voiceAIId ||
                            (queue.queueAIAudio.voiceCloneId
                                ? resolveVoiceIdFromClone(queue.queueAIAudio.voiceCloneId)
                                : null) ||
                            (queue.queueAIAudio.voicePromptId
                                ? resolveVoiceIdFromPrompt(queue.queueAIAudio.voicePromptId)
                                : null);
                        if (!formValues.selectedVoice) {
                            formValues.selectedVoice = primaryQueueVoiceId || "";
                        }
                        if (queue.queueAIAudio.voiceAISecondId) {
                            formValues.selectedSecondVoice = queue.queueAIAudio.voiceAISecondId;
                        }
                        if (queue.queueAIAudio.audioSpeed) {
                            setAudioSpeed(queue.queueAIAudio.audioSpeed);
                            formValues.audioSpeed = queue.queueAIAudio.audioSpeed;
                        }
                    }

                    if (audio) {
                        if (!formValues.textObject) {
                            formValues.textObject = audio.name;
                        }
                        if (!formValues.selectedVoice) {
                            // Resolve voiceCloneId/voicePromptId to VoiceAI ID for dropdown
                            formValues.selectedVoice =
                                audio.voiceAIId ||
                                (audio.voiceCloneId ? resolveVoiceIdFromClone(audio.voiceCloneId) : null) ||
                                (audio.voicePromptId ? resolveVoiceIdFromPrompt(audio.voicePromptId) : null) ||
                                "";
                        }
                        if (!formValues.selectedSecondVoice && audio.voiceAISecondId) {
                            formValues.selectedSecondVoice = audio.voiceAISecondId;
                        }
                        // In conversation mode, only take the audio's model if it supports dialogue
                        if (audio.model && (!hasDialogLines || supportsDialogue(audio.model))) {
                            formValues.audioModel = audio.model;
                        }
                        formValues.selectedTextType = audio.isDialog ? "isdialog" : "isText";
                        setLanguageCode(audio.language || null);
                        // Set language from audio if not already set from queue (important for manually created parents)
                        // audio.language might be a language AI ID (if stored as ID) or a code (if stored as code)
                        // Try to find matching language AI ID from the language code or use audio.language directly if it's already an ID
                        if (!formValues.selectedContentLanguage && audio.language) {
                            // Get languagesAI from global state (accessed via closure)
                            const languagesAI = data?.languagesAI || [];
                            // Check if audio.language is a language AI ID (check if it exists in languagesAI)
                            // languagesAI has structure: { value: string (ID), label: string, code: string }
                            const languageFromCode = languagesAI.find(
                                (lang: any) => lang.code === audio.language || lang.value === audio.language,
                            );
                            if (languageFromCode) {
                                // Use the language AI ID (stored in 'value' property)
                                formValues.selectedContentLanguage = languageFromCode.value;
                            } else {
                                // Fallback: assume audio.language is already a language AI ID
                                formValues.selectedContentLanguage = audio.language;
                            }
                        }
                    }

                    if (formValues.audioSpeed === undefined || formValues.audioSpeed === null) {
                        formValues.audioSpeed = 1.0;
                    }

                    if (formValues.audioSpeed !== undefined && formValues.audioSpeed !== null) {
                        setAudioSpeed(formValues.audioSpeed);
                    }

                    const mainTextObject = formValues.textObject || audio?.name || "";

                    // Also check project association for single audio (non-variant case)
                    if (audio && !resolvedVariants.length && audio.id) {
                        const isInProject = !!audio.projectAIId;
                        setCapsuleProjectInfo((prev) => ({
                            ...prev,
                            [audio.id]: {
                                isInProject,
                            },
                        }));
                    }

                    // Load ALL variants into capsules array (no special first capsule)
                    if (resolvedVariants.length > 0) {
                        capsulesToSet = resolvedVariants.map((variant: any, index: number) => {
                            const normalizedVariant = normalizeDialogLines(variant.dialogLines);
                            // Convert literal \n to actual newlines - prioritize raw text over normalized text
                            const variantText = (variant.text || normalizedVariant.fullText || "").replace(
                                /\\n/g,
                                "\n",
                            );

                            // Title logic: if only 1 variant, use mainTextObject directly
                            // If multiple variants, use "mainTextObject - X" format
                            let capsuleTitle: string;
                            if (resolvedVariants.length === 1) {
                                capsuleTitle = variant.name || mainTextObject;
                            } else {
                                capsuleTitle = variant.name || `${mainTextObject} - ${index + 1}`;
                            }

                            // Determine if this is actually a dialog (multiple voices) or narrative (single voice)
                            const isActualDialog = normalizedVariant.dialogLines.length > 1 || variant.isDialog;

                            // For voice assignment:
                            // - If it's a dialog (multiple lines), use dialogLines[0]?.voiceId (first voice in dialog)
                            // - If it's narrative (single voice), prioritize variant.voiceAIId or resolved voiceCloneId/voicePromptId (the actual voice used)
                            //   This prevents using stale dialogLines voice data from previous conversation mode
                            const resolvedVariantCloneId = variant.voiceCloneId
                                ? resolveVoiceIdFromClone(variant.voiceCloneId)
                                : null;
                            const resolvedVariantPromptId = variant.voicePromptId
                                ? resolveVoiceIdFromPrompt(variant.voicePromptId)
                                : null;
                            const capsuleVoice = isActualDialog
                                ? normalizedVariant.dialogLines[0]?.voiceId ||
                                  variant.voiceAIId ||
                                  resolvedVariantCloneId ||
                                  resolvedVariantPromptId ||
                                  formValues.selectedVoice ||
                                  ""
                                : variant.voiceAIId ||
                                  resolvedVariantCloneId ||
                                  resolvedVariantPromptId ||
                                  normalizedVariant.dialogLines[0]?.voiceId ||
                                  formValues.selectedVoice ||
                                  "";

                            return {
                                id: variant.id || `variant-${audio.id}-${index + 1}`,
                                textValue: variantText,
                                textObject: capsuleTitle,
                                selectedContentLanguage:
                                    queue?.queueAIText?.languageAIId || formValues.selectedContentLanguage || "",
                                selectedVoice: capsuleVoice,
                                selectedSecondVoice: variant.voiceAISecondId || formValues.selectedSecondVoice || "",
                                selectedTextType: isActualDialog ? "isdialog" : "isText",
                                audioSpeed: variant.audioSpeed ?? formValues.audioSpeed ?? 1.0,
                                audioModel: variant.model || formValues.audioModel || "",
                                dialogLines: normalizedVariant.dialogLines,
                                useMultipleNarrators: normalizedVariant.dialogLines.length > 0,
                                textSegments: normalizedVariant.segments,
                                audioId: variant.id,
                                audioUrl: variant.audioUrl || null,
                                audioName: variant.name ? variant.name : undefined, // Store original database name (not used in player)
                            };
                        });
                    } else {
                        // Legacy format: parse capsules from text
                        const legacyCapsules = parseLegacyCapsules(
                            audio?.text || queue?.queueAIText?.textObject,
                            mainTextObject,
                        );
                        if (legacyCapsules.length > 0) {
                            // ALL legacy capsules go into capsules array (no special first)
                            capsulesToSet = legacyCapsules.map((capsule, index) => ({
                                id: `legacy-${audio.id}-${index + 1}`,
                                // Convert literal \n to actual newlines
                                textValue: capsule.content.replace(/\\n/g, "\n"),
                                textObject: capsule.title, // Use parsed title from parseLegacyCapsules
                                selectedContentLanguage:
                                    formValues.selectedContentLanguage || queue?.queueAIText?.languageAIId || "",
                                selectedVoice: formValues.selectedVoice || "",
                                selectedSecondVoice: formValues.selectedSecondVoice || "",
                                selectedTextType: "isText",
                                audioSpeed: formValues.audioSpeed ?? 1.0,
                                audioModel: formValues.audioModel || "",
                                dialogLines: [],
                                useMultipleNarrators: false,
                                textSegments: [],
                                audioUrl: null,
                            }));
                        } else if (text.trim()) {
                            // Single audio without variants - create one capsule
                            // Use mainTextObject directly (no numbering for single capsule)
                            const normalizedSingle = normalizeDialogLines(audio?.dialogLines);
                            const isActualDialog = normalizedSingle.dialogLines.length > 1 || audio.isDialog;

                            // For voice assignment:
                            // - If it's a dialog (multiple lines), use dialogLines[0]?.voiceId (first voice in dialog)
                            // - If it's narrative (single voice), prioritize audio.voiceAIId or resolved voiceCloneId/voicePromptId (the actual voice used)
                            //   This prevents using stale dialogLines voice data from previous conversation mode
                            const resolvedAudioCloneId = audio.voiceCloneId
                                ? resolveVoiceIdFromClone(audio.voiceCloneId)
                                : null;
                            const resolvedAudioPromptId = audio.voicePromptId
                                ? resolveVoiceIdFromPrompt(audio.voicePromptId)
                                : null;
                            const capsuleVoice = isActualDialog
                                ? normalizedSingle.dialogLines[0]?.voiceId ||
                                  audio.voiceAIId ||
                                  resolvedAudioCloneId ||
                                  resolvedAudioPromptId ||
                                  formValues.selectedVoice ||
                                  ""
                                : audio.voiceAIId ||
                                  resolvedAudioCloneId ||
                                  resolvedAudioPromptId ||
                                  normalizedSingle.dialogLines[0]?.voiceId ||
                                  formValues.selectedVoice ||
                                  "";

                            capsulesToSet = [
                                {
                                    id: audio.id || `single-${Date.now()}-1`,
                                    // Convert literal \n to actual newlines - prioritize raw text over normalized text
                                    textValue: (text || normalizedSingle.fullText).replace(/\\n/g, "\n"),
                                    textObject: mainTextObject,
                                    selectedContentLanguage:
                                        formValues.selectedContentLanguage || queue?.queueAIText?.languageAIId || "",
                                    selectedVoice: capsuleVoice,
                                    selectedSecondVoice: formValues.selectedSecondVoice || "",
                                    selectedTextType: isActualDialog ? "isdialog" : "isText",
                                    audioSpeed: formValues.audioSpeed ?? 1.0,
                                    audioModel: formValues.audioModel || "",
                                    dialogLines: normalizedSingle.dialogLines,
                                    useMultipleNarrators: normalizedSingle.dialogLines.length > 0,
                                    textSegments: normalizedSingle.segments,
                                    audioId: audio.id,
                                    audioUrl: audio.audioUrl || null,
                                    audioName: audio.name ? audio.name : undefined, // Store original database name (not used in player)
                                },
                            ];
                        }
                    }

                    // CRITICAL: For capsules in conversation mode, if they have selectedVoice but no textSegments,
                    // create a default segment covering the entire text so the voice badge appears correctly
                    // This handles the case when switching from narrative to conversation mode
                    // The podcastType is already determined above, so we can use it
                    const willBeConversationMode = formValues.podcastType === "conversation";

                    capsulesToSet = capsulesToSet.map((capsule) => {
                        // If in conversation mode, has selectedVoice, has text, but no segments, create default segment
                        // This ensures the voice badge shows the correct voice even when there are no dialogLines
                        if (
                            willBeConversationMode &&
                            capsule.selectedVoice &&
                            capsule.selectedVoice.trim() !== "" &&
                            capsule.textValue &&
                            capsule.textValue.trim().length > 0 &&
                            (!capsule.textSegments || capsule.textSegments.length === 0)
                        ) {
                            return {
                                ...capsule,
                                textSegments: [
                                    {
                                        id: `default-seg-${capsule.id}-${Date.now()}`,
                                        startIndex: 0,
                                        endIndex: capsule.textValue.length,
                                        voiceId: capsule.selectedVoice,
                                        text: capsule.textValue,
                                    },
                                ],
                            };
                        }
                        return capsule;
                    });

                    console.log("[TextToSpeech] Setting capsules:", {
                        count: capsulesToSet.length,
                        capsules: capsulesToSet.map((c) => ({
                            id: c.id,
                            title: c.textObject,
                            hasAudioUrl: !!c.audioUrl,
                            audioUrl: c.audioUrl,
                            textLength: c.textValue?.length || 0,
                            hasSegments: !!(c.textSegments && c.textSegments.length > 0),
                            selectedVoice: c.selectedVoice,
                        })),
                    });

                    setCapsulesSafely(capsulesToSet);

                    // Initialize prefix ref with loaded form value to prevent unnecessary sync on initial load
                    if (formValues.textObject) {
                        lastPrefixRef.current = formValues.textObject;
                        setCurrentTextObject(formValues.textObject);
                    }

                    // Reconcile selection after refetch - ensure a valid selection
                    const opts = capsulesToSet.map((cap, idx) => ({
                        id: cap.audioId || cap.id,
                        // Use audioName (database value) for player, fallback to textObject if audioName not available
                        title: cap.audioName || cap.textObject || `Capsule ${idx + 1}`,
                        url: cap.audioUrl || null,
                    }));

                    const selected = opts.find((o) => o.id === selectedAudioId);
                    if (!selected || !selected.url) {
                        const firstWithUrl = opts.find((o) => o.url);
                        if (firstWithUrl) {
                            setSelectedAudioId(firstWithUrl.id);
                            setAudioUrl(firstWithUrl.url!);
                            setAudioTitle(firstWithUrl.title);
                        } else {
                            setAudioUrl(null);
                        }
                    }

                    // Set first capsule as focused
                    if (capsulesToSet.length > 0) {
                        setFocusedCapsuleId(capsulesToSet[0].id);
                    }

                    // Resolve voiceCloneIds/voicePromptIds to VoiceAI IDs for collectVoiceIds
                    const resolvedPrimaryVoiceId =
                        formValues.selectedVoice ||
                        audio?.voiceAIId ||
                        (audio?.voiceCloneId ? resolveVoiceIdFromClone(audio.voiceCloneId) : null) ||
                        (audio?.voicePromptId ? resolveVoiceIdFromPrompt(audio.voicePromptId) : null) ||
                        queue?.queueAIAudio?.voiceAIId ||
                        (queue?.queueAIAudio?.voiceCloneId
                            ? resolveVoiceIdFromClone(queue.queueAIAudio.voiceCloneId)
                            : null) ||
                        (queue?.queueAIAudio?.voicePromptId
                            ? resolveVoiceIdFromPrompt(queue.queueAIAudio.voicePromptId)
                            : null);

                    const derivedVoiceIds = collectVoiceIds({
                        primaryVoiceId: resolvedPrimaryVoiceId,
                        capsules: capsulesToSet,
                        additionalVoiceIds: [
                            formValues.selectedSecondVoice,
                            audio?.voiceAISecondId,
                            queue?.queueAIAudio?.voiceAISecondId,
                        ],
                    });
                    setSelectedVoices(derivedVoiceIds);

                    // Ensure language is set - it's critical for form initialization
                    // If still not set, try one more time from audio or queue
                    if (!formValues.selectedContentLanguage) {
                        if (queue?.queueAIText?.languageAIId) {
                            formValues.selectedContentLanguage = queue.queueAIText.languageAIId;
                        } else if (audio?.language) {
                            // Try to find language AI ID from audio.language
                            const languagesAI = data?.languagesAI || [];
                            const languageFromCode = languagesAI.find(
                                (lang: any) => lang.code === audio.language || lang.value === audio.language,
                            );
                            if (languageFromCode) {
                                formValues.selectedContentLanguage = languageFromCode.value;
                            } else {
                                // Fallback: assume audio.language is already a language AI ID
                                formValues.selectedContentLanguage = audio.language;
                            }
                        }
                    }

                    if (Object.keys(formValues).length > 0) {
                        setValues(formValues);
                    }

                    if (waiting || (queue && !queue.queueAIAudioId)) {
                        if (queue && !queue.queueAIAudioId) {
                            if (!silent) {
                                setShowStatusModal(false);
                                setLoadingTTSGeneration(false);
                            }
                        }

                        if (queue && queue.queueAIAudioId) {
                            if (typeof audioAI.rank === "number" && !silent) {
                                setWaitingRank(audioAI.rank);
                            }
                            setTimeout(fetchAudioAI, STATUS_POLL_INTERVAL_MS);
                        }
                    } else {
                        if (queue?.status === "failed") {
                            if (!silent) {
                                setLoadingTTSGeneration(false);
                                setErrorReason("generic");
                                setGenerationStatus("error");
                                resetCancelling();
                            }
                            return;
                        }

                        // The form was restored above, so the audio can be relaunched as is.
                        if (queue?.status === "canceled") {
                            if (!silent) {
                                setLoadingTTSGeneration(false);
                                setShowStatusModal(false);
                                setWaitingRank(null);
                                confirmCanceled();
                            }
                            return;
                        }

                        if (!silent) {
                            setLoadingTTSGeneration(false);
                            setWaitingRank(null);
                        }

                        // Set audio from first capsule with audio URL (parent doesn't have audio)
                        if (capsulesToSet.length > 0) {
                            const firstCapsuleWithAudio = capsulesToSet.find((cap) => cap.audioUrl);
                            console.log(
                                "[handleAudioAIAwaiting] Setting audio from first capsule:",
                                firstCapsuleWithAudio,
                            );
                            if (firstCapsuleWithAudio) {
                                // Use audioName (database value) for player, fallback to textObject if audioName not available
                                const displayTitle =
                                    firstCapsuleWithAudio.audioName || firstCapsuleWithAudio.textObject || "";
                                setAudioTitle(displayTitle);
                                setAudioUrl(firstCapsuleWithAudio.audioUrl || null);
                                setSelectedAudioId(firstCapsuleWithAudio.audioId || null);
                                console.log("[handleAudioAIAwaiting] Set audio:", {
                                    url: firstCapsuleWithAudio.audioUrl,
                                    title: displayTitle,
                                    audioName: firstCapsuleWithAudio.audioName,
                                    textObject: firstCapsuleWithAudio.textObject,
                                    id: firstCapsuleWithAudio.audioId,
                                });
                            }
                        }

                        if (!silent) {
                            setGenerationStatus("success");
                            refetchTokens();
                            resetCancelling();
                        }
                    }
                } catch (error) {
                    console.error("Error fetching audio AI data:", error);
                    // Network/WAF blips are not generation failures — keep waiting and retry.
                    if (!silent && !isStale()) {
                        setGenerationStatus("waiting");
                        setTimeout(fetchAudioAI, STATUS_POLL_INTERVAL_MS);
                    }
                } finally {
                    // ALWAYS clear the flag so future refetches aren't blocked
                    loadingAudioIdRef.current = null;
                }
            };

            fetchAudioAI();
        },
        [
            setLoadingTTSGeneration,
            setShowStatusModal,
            setGenerationStatus,
            setWaitingRank,
            setCapsulesSafely,
            refetchTokens,
            setSelectedVoices,
            setAudioSpeed,
            setLanguageCode,
            confirmCanceled,
            resetCancelling,
            selectedAudioId,
            setSelectedAudioId,
            setAudioUrl,
            setAudioTitle,
            navigate,
            data?.languagesAI,
            resolveVoiceIdFromClone,
            resolveVoiceIdFromPrompt,
        ],
    );

    const handleAddToProject = async (projectId: string, newProjectName: string, selectedVariantIds?: string[]) => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        // Get capsules to add - either selected variants or all capsules with audio URLs
        let capsulesToAdd: Capsule[];
        if (selectedVariantIds && selectedVariantIds.length > 0) {
            // Filter capsules by selected variant IDs
            capsulesToAdd = capsules.filter(
                (cap) => (cap.audioId || cap.id) && selectedVariantIds.includes(cap.audioId || cap.id),
            );
        } else {
            // Get all capsules with audio URLs (for single audio case)
            capsulesToAdd = capsules.filter((cap) => cap.audioUrl && (cap.audioId || cap.id));
        }

        if (capsulesToAdd.length === 0) {
            console.warn("[handleAddToProject] No capsules to add found");
            return;
        }

        console.log("[handleAddToProject] Adding capsules to project:", {
            projectId,
            newProjectName,
            capsuleCount: capsulesToAdd.length,
            capsuleIds: capsulesToAdd.map((c) => c.audioId || c.id),
        });

        try {
            if (projectId === "new") {
                // Create new project with the first audio
                const firstCapsule = capsulesToAdd[0];
                const firstCapsuleId = firstCapsule.audioId || firstCapsule.id;

                const createdProject = await createProjectAI({
                    token: token,
                    name: newProjectName,
                    audioAIId: firstCapsuleId,
                });

                // Validate that we got a project ID
                if (!createdProject?.id || createdProject.id.trim() === "") {
                    console.error("[handleAddToProject] Failed to create project - no project ID returned");
                    throw new Error("Failed to create project: no project ID returned");
                }

                const projectIdString = createdProject.id;

                // Add remaining audios to the newly created project
                if (capsulesToAdd.length > 1) {
                    for (let i = 1; i < capsulesToAdd.length; i++) {
                        const capsule = capsulesToAdd[i];
                        try {
                            await addItemToProjectAI({
                                token: token,
                                projectId: projectIdString,
                                itemId: capsule.audioId || capsule.id,
                                itemType: AI_MODEL_TYPES.AUDIO,
                            });
                        } catch (addError) {
                            console.error(
                                `[handleAddToProject] Error adding capsule ${capsule.id} to project:`,
                                addError,
                            );
                            // Continue with other capsules even if one fails
                        }
                    }
                }
            } else {
                // Add all selected audios to existing project
                for (const capsule of capsulesToAdd) {
                    try {
                        await addItemToProjectAI({
                            token: token,
                            projectId,
                            itemId: capsule.audioId || capsule.id,
                            itemType: AI_MODEL_TYPES.AUDIO,
                        });
                    } catch (addError) {
                        console.error(`[handleAddToProject] Error adding capsule ${capsule.id} to project:`, addError);
                        // Continue with other capsules even if one fails
                    }
                }
            }

            await refreshProjects();

            // Refresh project info for added capsules
            for (const capsule of capsulesToAdd) {
                const capsuleId = capsule.audioId || capsule.id;
                if (capsuleId) {
                    try {
                        const updatedAudioAI = await getWaitingAudioAI({ token, id: capsuleId });
                        if (updatedAudioAI?.audioAI) {
                            setCapsuleProjectInfo((prev) => ({
                                ...prev,
                                [capsuleId]: {
                                    isInProject: !!updatedAudioAI.audioAI.projectAIId,
                                },
                            }));
                        }
                    } catch (err) {
                        console.error("Error refreshing project info for capsule:", err);
                    }
                }
            }

            // Show success notification
            showNotification({
                message: t("projectItemsAddedSuccessfully") || "Items added to project successfully",
                type: "success",
            });
        } catch (error) {
            console.error("[handleAddToProject] Error adding to project:", error);
            showNotification({
                message: t("errorAddingToProject") || "An error occurred. Please try again or contact support.",
                type: "error",
            });
        }
    };

    const handleSubmit = async (values: FormValues) => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }

        setLoadingTTSGeneration(true);

        // Helper function to submit a capsule for audio generation
        const submitCapsule = async (capsule: Capsule, parentId: string) => {
            // In narrative mode, use capsule's model if available, otherwise use form values
            const isNarrativeMode = values.podcastType === "narrative";
            const capsuleModel =
                isNarrativeMode && capsule.audioModel && capsule.audioModel.trim() !== ""
                    ? capsule.audioModel
                    : values.audioModel;

            // Convert segments to dialog lines for this capsule
            // ONLY in conversation mode - ignore segments in narrative mode
            let capsuleDialogLines: DialogLine[] = [];
            let capsuleShouldUseDialogLines = false;

            // Only process segments/dialog lines if we're in conversation mode
            if (!isNarrativeMode && supportsDialogue(capsuleModel)) {
                if (capsule.textSegments && capsule.textSegments.length > 0) {
                    // CRITICAL: Normalize segments before conversion to ensure indices match current text
                    // This handles cases where text was edited but segments weren't properly rebased
                    const normalizedSegments = capsule.textSegments
                        .map((seg) => ({
                            ...seg,
                            // Recompute text from current text to ensure it's accurate
                            text: capsule.textValue.substring(
                                Math.max(0, Math.min(seg.startIndex, capsule.textValue.length)),
                                Math.max(0, Math.min(seg.endIndex, capsule.textValue.length)),
                            ),
                        }))
                        .filter(
                            (seg) =>
                                seg.startIndex >= 0 &&
                                seg.endIndex <= capsule.textValue.length &&
                                seg.startIndex < seg.endIndex,
                        );

                    if (normalizedSegments.length === 0) {
                        console.warn("[submitCapsule] ⚠️ No valid segments after normalization, skipping dialog lines");
                    } else {
                        // Use capsule's voice if available, otherwise fall back to form values
                        const defaultVoice =
                            capsule.selectedVoice ||
                            values.selectedVoice ||
                            (normalizedSegments.length > 0 && normalizedSegments[0].voiceId
                                ? normalizedSegments[0].voiceId
                                : "");
                        capsuleDialogLines = convertSegmentsToDialogLines(
                            capsule.textValue,
                            normalizedSegments,
                            defaultVoice,
                        );
                        capsuleShouldUseDialogLines = capsuleDialogLines.length > 0;
                    }
                }
            }

            // In narrative mode, use capsule's voice/speed if available, otherwise use form values
            // CRITICAL: In narrative mode, prioritize capsule.selectedVoice over textSegments
            // This ensures user selections in narrative mode are respected, even if textSegments exist from conversation mode
            const capsuleVoiceId = isNarrativeMode
                ? capsule.selectedVoice?.trim() || values.selectedVoice || ""
                : capsuleShouldUseDialogLines && capsuleDialogLines.length > 0
                  ? capsuleDialogLines[0].voiceId || values.selectedVoice
                  : values.selectedVoice;

            const capsuleSpeed =
                isNarrativeMode && capsule.audioSpeed !== undefined && capsule.audioSpeed !== null
                    ? capsule.audioSpeed
                    : values.audioSpeed;

            // In narrative mode, never use dialog - always single voice
            const capsuleIsDialog = isNarrativeMode
                ? false
                : capsuleShouldUseDialogLines && capsuleDialogLines.length > 1;

            // Prepare final dialogLines for API
            // In narrative mode, never send dialog lines
            const finalDialogLines = isNarrativeMode
                ? undefined
                : capsuleShouldUseDialogLines && capsuleDialogLines.length > 0
                  ? capsuleDialogLines.map((line) => ({
                        text: line.text,
                        voiceId: resolveVoiceIdForApi(line.voiceId) || line.voiceId,
                    }))
                  : undefined;

            const apiVoiceId = resolveVoiceIdForApi(capsuleVoiceId) || capsuleVoiceId;
            const apiSecondVoiceId = resolveVoiceIdForApi(values.selectedSecondVoice);

            return await createAudioQueueAI({
                token: token,
                voiceId: apiVoiceId || "",
                // In narrative mode, always send text. In conversation mode, send empty string if using dialog lines
                text: isNarrativeMode ? capsule.textValue : capsuleShouldUseDialogLines ? "" : capsule.textValue,
                secondVoiceId: apiSecondVoiceId || null,
                isDialog: capsuleIsDialog,
                textTitle: capsule.textObject || values.textObject,
                audioSpeed: capsuleSpeed,
                language: values.selectedContentLanguage,
                model: capsuleModel || undefined,
                dialogLines: finalDialogLines,
                parentId: parentId,
            });
        };

        // Show status modal immediately
        setLoadingTTSGeneration(true);
        setShowStatusModal(true);
        setGenerationStatus("waiting");
        setErrorReason("generic");
        setWaitingRank(null);
        statusPollFailuresRef.current = 0;

        let parentId = id; // From URL if exists

        // If no parent exists, create one (metadata only, no audio generation)
        if (!parentId) {
            // Combine all capsule texts for parent record
            const combinedText = capsules.map((cap) => cap.textValue).join("\n\n");

            const parentResponse = await createManualAudioAI({
                token: token,
                text: combinedText,
                audioUrl: "", // Empty string - parent has no audio
                textTitle: values.textObject,
                language: values.selectedContentLanguage,
                voiceId: resolveVoiceIdForApi(values.selectedVoice) || null,
                secondVoiceId: resolveVoiceIdForApi(values.selectedSecondVoice) || null,
                isDialog: false,
                model: values.audioModel || null,
                parentId: null,
            });

            if (!parentResponse.id) {
                console.error("Error creating parent audio AI");
                setLoadingTTSGeneration(false);
                setShowStatusModal(false);
                return;
            }

            parentId = parentResponse.id;
        }

        // Collect all audio IDs for variants
        const allAudioIds: string[] = [];

        // Create variant AudioAI for ALL capsules (including first)
        try {
            for (const capsule of capsules) {
                const variantResponse = await submitCapsule(capsule, parentId);
                if (variantResponse.id) {
                    allAudioIds.push(variantResponse.id);
                }
            }
        } catch (err) {
            // If some capsules were already queued, keep monitoring those instead of a hard fail.
            if (allAudioIds.length === 0) {
                setLoadingTTSGeneration(false);
                if (isQuotaError(err)) {
                    setErrorReason("quota");
                    refetchTokens();
                } else {
                    setErrorReason("generic");
                }
                setGenerationStatus("error");
                setGeneratingParentId(null);
                return;
            }
            console.warn(
                "[TextToSpeech] Enqueue interrupted after partial success; monitoring queued audios:",
                allAudioIds.length,
                err,
            );
        }

        // Only now that every capsule is queued: a cancel sent earlier would miss the later ones.
        setGeneratingParentId(parentId || null);

        // Don't navigate during generation - wait until all generation completes
        // This prevents the page from reloading and clearing capsules before all variants are created
        // Navigation will happen after generation completes in checkAllAudioStatus

        const clearStatusPollTimeout = () => {
            if (checkStatusTimeoutRef.current) {
                clearTimeout(checkStatusTimeoutRef.current);
                checkStatusTimeoutRef.current = null;
            }
        };

        const showStatusUnavailable = () => {
            setLoadingTTSGeneration(false);
            setErrorReason("statusUnavailable");
            setGenerationStatus("error");
            setShowStatusModal(true);
            isCheckingStatusRef.current = false;
            clearStatusPollTimeout();
            setGeneratingParentId(null);
            resetCancelling();
            // Do not navigate here: redirecting while the API is flaky unmounts this modal
            // (bootstrap spinner) and the user loses the message. Navigate/reload on close instead.
            statusUnavailableParentIdRef.current = parentId || null;
        };

        // Monitor all audio generations
        const pollGeneration = pollGenerationRef.current;
        const isStale = () => pollGenerationRef.current !== pollGeneration;

        const checkAllAudioStatus = async () => {
            // Prevent multiple simultaneous checks
            if (isCheckingStatusRef.current || isStale()) {
                return;
            }

            isCheckingStatusRef.current = true;

            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    setLoadingTTSGeneration(false);
                    setShowStatusModal(false);
                    isCheckingStatusRef.current = false;
                    return;
                }

                let allCompleted = true;
                let hasError = false;
                let hasCanceled = false;
                let maxRank = 0;

                // Check status of all audios
                for (const audioId of allAudioIds) {
                    const audioAI = await getWaitingAudioAI({
                        token: token,
                        id: audioId,
                    });

                    if (!audioAI || !audioAI.audioAI) {
                        allCompleted = false;
                        continue;
                    }

                    if (audioAI?.failed) {
                        hasError = true;
                        allCompleted = false;
                        break;
                    }

                    // The whole batch is canceled at once (the parent is what gets canceled).
                    if (audioAI?.canceled) {
                        hasCanceled = true;
                        allCompleted = false;
                        break;
                    }

                    const { waiting, queueAI: queue } = audioAI;

                    if (waiting || (queue && !queue.queueAIAudioId)) {
                        allCompleted = false;
                        if (queue && queue.queueAIAudioId) {
                            if (typeof audioAI.rank === "number" && audioAI.rank > maxRank) {
                                maxRank = audioAI.rank;
                            }
                        }
                    }
                }

                if (isStale()) {
                    isCheckingStatusRef.current = false;
                    return;
                }

                // Successful round-trip — reset transient poll failures
                statusPollFailuresRef.current = 0;

                // Update waiting rank (show highest rank adjusted for multiple audios generating)
                // Formula: rank - (number of audios generating - 1)
                // This reflects the proper queue position accounting for all audios in the batch
                if (maxRank > 0) {
                    const numberOfAudiosGenerating = allAudioIds.length;
                    const adjustedRank = maxRank - (numberOfAudiosGenerating - 1);
                    setWaitingRank(Math.max(1, adjustedRank)); // Ensure rank is at least 1
                }

                if (hasError) {
                    setLoadingTTSGeneration(false);
                    setErrorReason("generic");
                    setGenerationStatus("error");
                    isCheckingStatusRef.current = false;
                    clearStatusPollTimeout();
                    setGeneratingParentId(null);
                    resetCancelling();
                    return;
                }

                // Canceled elsewhere (another tab): conclude here. Canceled from this page,
                // handleCancelGeneration concludes once its request returns; until then, keep
                // polling, so a failed cancel request cannot leave the modal hanging.
                // The capsules are left as typed in both cases: they are the user's input, and
                // reloading the parent would replace them with what was stored before this run.
                if (hasCanceled && !cancelRequestedRef.current) {
                    setLoadingTTSGeneration(false);
                    setWaitingRank(null);
                    setShowStatusModal(false);
                    isCheckingStatusRef.current = false;
                    clearStatusPollTimeout();
                    setGeneratingParentId(null);
                    confirmCanceled();
                    return;
                }

                if (allCompleted) {
                    // All audios are ready
                    setGeneratingParentId(null);
                    resetCancelling();
                    setLoadingTTSGeneration(false);
                    setWaitingRank(null);
                    setGenerationStatus("success");
                    setShowStatusModal(false);
                    refetchTokens();
                    isCheckingStatusRef.current = false;
                    clearStatusPollTimeout();

                    // Navigate to parent ID if we created a new parent (only after generation completes)
                    // This ensures all variants are created before the page reloads
                    if (!id && parentId) {
                        navigate(`/create/text-to-speech/${parentId}`, { replace: true });
                    } else if (parentId) {
                        // If we already have an ID, just refetch to get updated data
                        // Make sure the guard won't block this refresh
                        loadingAudioIdRef.current = null; // (defensive) clear any stale lock
                        await handleAudioAIAwaiting(parentId, (vals) => setValuesRef.current(vals), {
                            force: true,
                            silent: true,
                        });
                    }
                    return;
                } else {
                    // Continue checking — 3s interval balances UX vs load / WAF false positives
                    // (also while this page's own cancel request is still running)
                    isCheckingStatusRef.current = false;
                    checkStatusTimeoutRef.current = setTimeout(() => {
                        checkAllAudioStatus();
                    }, STATUS_POLL_INTERVAL_MS);
                }
            } catch (error) {
                console.error("Error checking audio status:", error);
                isCheckingStatusRef.current = false;
                if (isStale()) return;
                statusPollFailuresRef.current += 1;

                if (statusPollFailuresRef.current >= MAX_STATUS_POLL_FAILURES && allAudioIds.length > 0) {
                    // Audios were queued; only status retrieval failed — not a generation failure.
                    showStatusUnavailable();
                    return;
                }

                // Keep "waiting" UI; a later poll may succeed
                setGenerationStatus("waiting");
                checkStatusTimeoutRef.current = setTimeout(() => {
                    checkAllAudioStatus();
                }, STATUS_POLL_INTERVAL_MS);
            }
        };

        // Start monitoring
        checkAllAudioStatus();
    };

    const handleSpeedChange = (newSpeed: number) => {
        setAudioSpeed(newSpeed);
    };

    // Handler for capsule voice change
    // CRITICAL: Always apply voice changes immediately, even if capsule already has a voice
    // This ensures user selections are respected when switching from conversation to narrative mode
    const handleCapsuleVoiceChange = useCallback((capsuleId: string, voiceId: string) => {
        setCapsules((prevCapsules) => {
            const updated = prevCapsules.map((cap) => {
                if (cap.id === capsuleId) {
                    // Always update the voice, even if it's the same or if capsule already had a voice
                    // This ensures voice changes from conversation mode are properly applied in narrative mode
                    return { ...cap, selectedVoice: voiceId };
                }
                return cap;
            });
            return updated;
        });
    }, []);

    // Handler for capsule speed change
    const handleCapsuleSpeedChange = useCallback((capsuleId: string, speed: number) => {
        setCapsules((prevCapsules) =>
            prevCapsules.map((cap) => (cap.id === capsuleId ? { ...cap, audioSpeed: speed } : cap)),
        );
    }, []);

    // Handler for capsule model change
    const handleCapsuleModelChange = useCallback((capsuleId: string, model: string) => {
        setCapsules((prevCapsules) =>
            prevCapsules.map((cap) => (cap.id === capsuleId ? { ...cap, audioModel: model } : cap)),
        );
    }, []);

    // Global voice apply handler - applies voice to ALL capsules with text
    const handleGlobalVoiceApply = useCallback((voiceId: string) => {
        setLastAppliedVoice(voiceId);

        // Apply voice to all capsules that have text
        setCapsules((prevCapsules) =>
            prevCapsules.map((capsule) => {
                if (capsule.textValue && capsule.textValue.trim().length > 0) {
                    // Create a single segment for all text
                    return {
                        ...capsule,
                        textSegments: [
                            {
                                id: `segment-${Date.now()}-${Math.random()}`,
                                startIndex: 0,
                                endIndex: capsule.textValue.length,
                                voiceId: voiceId,
                                text: capsule.textValue,
                            },
                        ],
                    };
                }
                return capsule;
            }),
        );
    }, []);

    const handleAddVoice = useCallback(
        (voiceId: string) => {
            if (voiceId && !selectedVoices.includes(voiceId)) {
                const newSelectedVoices = [...selectedVoices, voiceId];
                setSelectedVoices(newSelectedVoices);

                // If this is the first voice being added and capsules have text but no voice segments,
                // automatically apply the voice to all capsules
                if (selectedVoices.length === 0) {
                    // Set the global default for new typing
                    setLastAppliedVoice(voiceId);

                    // Check if any capsule has text but no voice segments
                    const hasCapsulesWithTextButNoVoice = capsules.some(
                        (capsule) =>
                            capsule.textValue &&
                            capsule.textValue.trim().length > 0 &&
                            (!capsule.textSegments || capsule.textSegments.length === 0),
                    );

                    if (hasCapsulesWithTextButNoVoice) {
                        // Automatically apply the first voice to all capsules with text
                        handleGlobalVoiceApply(voiceId);
                    }
                }
            }
        },
        [selectedVoices, capsules, handleGlobalVoiceApply],
    );

    const handleRemoveVoice = (voiceId: string) => {
        setSelectedVoices(selectedVoices.filter((id) => id !== voiceId));
        // Also remove any segments using this voice from all capsules
        setCapsules((prevCapsules) =>
            prevCapsules.map((capsule) => ({
                ...capsule,
                textSegments: capsule.textSegments.filter((seg) => seg.voiceId !== voiceId),
            })),
        );
    };

    // Functions to manage capsules
    const handleAddCapsule = useCallback((currentValues: FormValues) => {
        setCapsules((prevCapsules) => {
            const mainTextObject = currentValues.textObject || "Capsule";
            const newIndex = prevCapsules.length + 1;

            // Title logic:
            // - If this will be the ONLY capsule (length 0 → 1), use mainTextObject directly
            // - If there will be multiple (length >= 1), use "mainTextObject - X" format
            let newCapsuleTitle: string;
            let updatedPrevCapsules = prevCapsules;

            if (prevCapsules.length === 0) {
                // This will be the first and only capsule
                newCapsuleTitle = mainTextObject;
            } else {
                // Multiple capsules: use "mainTextObject - X" format
                newCapsuleTitle = `${mainTextObject} - ${newIndex}`;

                // Also update existing capsules if first capsule doesn't have numbering
                updatedPrevCapsules = prevCapsules.map((capsule, index) => {
                    // If first capsule doesn't have " - 1", add numbering to all
                    if (index === 0 && !capsule.textObject.includes(" - ")) {
                        return {
                            ...capsule,
                            textObject: `${mainTextObject} - 1`,
                        };
                    }
                    return capsule;
                });
            }

            const newCapsuleId = `capsule-${Date.now()}-${Math.random()}`;

            // Determine voice/model/speed for new capsule:
            // - If this is the first capsule (no other capsules exist), use form-level values
            // - If other capsules exist and any have individual values, leave empty (user must focus to set)
            // - Otherwise, use form-level values (first selection applies to all)
            const hasAnyCapsuleVoice = prevCapsules.some((cap) => cap.selectedVoice && cap.selectedVoice.trim() !== "");
            const hasAnyCapsuleModel = prevCapsules.some((cap) => cap.audioModel && cap.audioModel.trim() !== "");
            const hasAnyCapsuleSpeed = prevCapsules.some(
                (cap) =>
                    cap.audioSpeed !== undefined &&
                    cap.audioSpeed !== null &&
                    cap.audioSpeed !== currentValues.audioSpeed,
            );

            const newCapsuleVoice = hasAnyCapsuleVoice ? "" : currentValues.selectedVoice;
            const newCapsuleModel = hasAnyCapsuleModel ? "" : currentValues.audioModel;
            const newCapsuleSpeed = hasAnyCapsuleSpeed ? (currentValues.audioSpeed ?? 1.0) : currentValues.audioSpeed;

            const newCapsule: Capsule = {
                id: newCapsuleId,
                textValue: "",
                textObject: newCapsuleTitle,
                selectedContentLanguage: currentValues.selectedContentLanguage,
                selectedVoice: newCapsuleVoice,
                selectedSecondVoice: currentValues.selectedSecondVoice,
                selectedTextType: currentValues.selectedTextType,
                audioSpeed: newCapsuleSpeed,
                audioModel: newCapsuleModel,
                dialogLines: [],
                useMultipleNarrators: false,
                textSegments: [],
                audioId: undefined,
                audioUrl: null,
            };

            // Focus on newly added capsule
            setFocusedCapsuleId(newCapsuleId);

            return [...updatedPrevCapsules, newCapsule];
        });
    }, []);

    const handleRemoveCapsule = useCallback(
        (capsuleId: string) => {
            setCapsules((prevCapsules) => prevCapsules.filter((cap) => cap.id !== capsuleId));
            setCapsuleAssignVoiceHandlers((prev) => {
                if (!prev[capsuleId]) {
                    return prev;
                }
                const { [capsuleId]: _removed, ...rest } = prev;
                return rest;
            });
            delete capsuleSelectionHandlersRef.current[capsuleId];
            delete capsuleAssignVoiceHandlersRef.current[capsuleId];
            delete capsuleSegmentsChangeHandlersRef.current[capsuleId];
            delete capsuleTextChangeHandlersRef.current[capsuleId];
        },
        [setCapsuleAssignVoiceHandlers],
    );

    // Handler to update capsule title - combines prefix (from form) with suffix (user input)
    const handleCapsuleTitleChange = useCallback((capsuleId: string, newSuffix: string, currentPrefix: string) => {
        setCapsules((prevCapsules) => {
            let newTitle: string;
            if (currentPrefix) {
                // Add a space between prefix and suffix if suffix doesn't start with whitespace
                const separator = newSuffix.length > 0 && newSuffix[0] !== " " ? " " : "";
                newTitle = `${currentPrefix}${separator}${newSuffix}`;
            } else {
                newTitle = newSuffix;
            }
            return prevCapsules.map((cap) => (cap.id === capsuleId ? { ...cap, textObject: newTitle } : cap));
        });
    }, []);

    // Handler to update capsule segments - memoized per capsule
    const getCapsuleSegmentsChangeHandler = useCallback((capsuleId: string) => {
        if (!capsuleSegmentsChangeHandlersRef.current[capsuleId]) {
            capsuleSegmentsChangeHandlersRef.current[capsuleId] = (segments: TextSegment[]) => {
                setCapsules((prevCapsules) =>
                    prevCapsules.map((cap) => (cap.id === capsuleId ? { ...cap, textSegments: segments } : cap)),
                );
            };
        }
        return capsuleSegmentsChangeHandlersRef.current[capsuleId];
    }, []);

    // Handler to update capsule text - memoized per capsule
    const getCapsuleTextChangeHandler = useCallback((capsuleId: string) => {
        if (!capsuleTextChangeHandlersRef.current[capsuleId]) {
            capsuleTextChangeHandlersRef.current[capsuleId] = (text: string) => {
                setCapsules((prevCapsules) =>
                    prevCapsules.map((cap) => (cap.id === capsuleId ? { ...cap, textValue: text } : cap)),
                );
            };
        }
        return capsuleTextChangeHandlersRef.current[capsuleId];
    }, []);

    // Handler for selection change in capsules - memoized per capsule
    const getCapsuleSelectionHandler = useCallback((capsuleId: string) => {
        if (!capsuleSelectionHandlersRef.current[capsuleId]) {
            capsuleSelectionHandlersRef.current[capsuleId] = (hasSelection: boolean) => {
                if (hasSelection) {
                    setActiveCapsuleId(capsuleId);
                    setHasTextSelection(true);
                } else {
                    setActiveCapsuleId((prevId) => {
                        const next = prevId === capsuleId ? null : prevId;
                        setHasTextSelection(Boolean(next));
                        return next;
                    });
                }
            };
        }
        return capsuleSelectionHandlersRef.current[capsuleId];
    }, []);

    // Handler for assignVoiceToSelection for capsules - memoized per capsule
    const getCapsuleAssignVoiceHandler = useCallback((capsuleId: string) => {
        if (!capsuleAssignVoiceHandlersRef.current[capsuleId]) {
            capsuleAssignVoiceHandlersRef.current[capsuleId] = (handler: (voiceId: string) => void) => {
                setCapsuleAssignVoiceHandlers((prev) => ({
                    ...prev,
                    [capsuleId]: handler,
                }));
            };
        }
        return capsuleAssignVoiceHandlersRef.current[capsuleId];
    }, []);

    // Language code mapping is handled in handleAudioAIAwaiting via setValues
    // This effect is kept for potential future use
    useEffect(() => {
        if (data && data.languagesAI && data.languagesAI.length > 0 && languageCode) {
            // Language selection is handled by Formik in the form component
        }
    }, [languageCode, data]);

    // Sync capsule titles when the global prefix (textObject) changes
    useEffect(() => {
        const newPrefix = currentTextObject || "";
        const oldPrefix = lastPrefixRef.current;

        if (newPrefix === oldPrefix) return;

        setCapsules((prev) => {
            const capsuleLabel = t("capsule") || "Capsule";
            const multi = prev.length > 1;

            return prev.map((cap, idx) => {
                const { suffix } = splitTitle(cap.textObject || "", oldPrefix, idx, capsuleLabel);

                // Normalize spacing: if we have a new prefix, ensure there's exactly one space
                // between prefix and a non-numbering suffix. For numbering-only suffix like " - 2"
                // we don't add an extra space.
                let nextTitle: string;

                if (!newPrefix) {
                    // No global prefix anymore: keep only the suffix *without* leading space
                    nextTitle = suffix.trimStart();
                } else {
                    if (!suffix || /^\s*-\s*\d+\s*$/.test(suffix)) {
                        // Only numbering (or empty): compose "Prefix" or "Prefix - X" if multiple
                        const numbering = multi ? ` - ${idx + 1}` : "";
                        nextTitle = `${newPrefix}${numbering}`;
                    } else {
                        // Free-text suffix: keep one space if suffix doesn't start with space or dash-number
                        const needsSpace = !suffix.startsWith(" ") && !suffix.startsWith("-");
                        nextTitle = `${newPrefix}${needsSpace ? " " : ""}${suffix.trimStart()}`;
                    }
                }

                return { ...cap, textObject: nextTitle };
            });
        });

        lastPrefixRef.current = newPrefix;
    }, [currentTextObject, t]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (checkStatusTimeoutRef.current) {
                clearTimeout(checkStatusTimeoutRef.current);
                checkStatusTimeoutRef.current = null;
            }
            isCheckingStatusRef.current = false;
        };
    }, []);

    // Build audio options list - all capsules are variants now
    const buildAudioOptions = useCallback((): AudioOption[] => {
        const audioOptionsList: AudioOption[] = [];

        // Add all capsules as audio options (no special parent with audio)
        capsules.forEach((capsule, index) => {
            if (capsule.audioId || capsule.id) {
                // Use audioName (database value) for player, fallback to textObject if audioName not available
                const displayTitle =
                    capsule.audioName || capsule.textObject || `${t("capsule") || "Capsule"} ${index + 1}`;
                audioOptionsList.push({
                    id: capsule.audioId || capsule.id,
                    title: displayTitle,
                    url: capsule.audioUrl || null,
                });
            }
        });

        console.log("[buildAudioOptions] Built audio options:", audioOptionsList);
        return audioOptionsList;
    }, [capsules, t]);

    // Reconcile selection whenever capsules change - reselect if current selection is invalid
    useEffect(() => {
        const opts = buildAudioOptions();
        const selectedCapsule = capsules.find((c) => c.audioId === selectedAudioId || c.id === selectedAudioId);
        const selectedHasUrl = !!selectedCapsule?.audioUrl;

        // If the selection is invalid (legacy id not found) OR has no url, pick a good one
        if (!selectedCapsule || !selectedHasUrl) {
            const firstWithUrl = opts.find((o) => o.url);
            if (firstWithUrl) {
                setSelectedAudioId(firstWithUrl.id);
                setAudioUrl(firstWithUrl.url!);
                setAudioTitle(firstWithUrl.title);
            } else {
                setAudioUrl(null);
            }
            return;
        }

        // Keep player synced if the URL for the selected capsule changes later
        if (audioUrl !== selectedCapsule.audioUrl) {
            setAudioUrl(selectedCapsule.audioUrl || null);
            // Use audioName (database value) for player, fallback to textObject if audioName not available
            const displayTitle = selectedCapsule.audioName || selectedCapsule.textObject || "";
            setAudioTitle(displayTitle);
        }
    }, [capsules, selectedAudioId, buildAudioOptions, audioUrl]);

    // Initialize selectedAudioId when options become available - resilient to invalid selections
    useEffect(() => {
        const options = buildAudioOptions();
        const selected = options.find((o) => o.id === selectedAudioId);
        const needReselect = !selected || !selected.url;

        if ((!selectedAudioId || needReselect) && options.length > 0) {
            const firstWithUrl = options.find((o) => o.url) || options[0];
            if (firstWithUrl) {
                setSelectedAudioId(firstWithUrl.id);
                setAudioUrl(firstWithUrl.url || null);
                setAudioTitle(firstWithUrl.title);
            }
        }
    }, [buildAudioOptions, selectedAudioId]);

    // Handle audio selection change
    const handleAudioSelect = useCallback(
        (audioId: string) => {
            console.log("[handleAudioSelect] Selecting audio:", { audioId, capsulesCount: capsules.length });
            setSelectedAudioId(audioId);
            // Find capsule by audioId or id
            const selectedCapsule = capsules.find((cap) => cap.audioId === audioId || cap.id === audioId);
            console.log("[handleAudioSelect] Found capsule:", selectedCapsule);

            if (selectedCapsule) {
                setAudioUrl(selectedCapsule.audioUrl || null);
                // Use audioName (database value) for player, fallback to textObject if audioName not available
                const displayTitle = selectedCapsule.audioName || selectedCapsule.textObject || "";
                setAudioTitle(displayTitle);
                console.log("[handleAudioSelect] Set audio:", {
                    url: selectedCapsule.audioUrl,
                    title: displayTitle,
                    audioName: selectedCapsule.audioName,
                    textObject: selectedCapsule.textObject,
                });
            }
        },
        [capsules],
    );

    const handleCancelGeneration = useCallback(() => {
        cancelRequestedRef.current = true;
        cancelGeneration(generatingParentId, () => {
            // The canceled variants are deleted: stop every loop still polling them.
            pollGenerationRef.current += 1;
            if (checkStatusTimeoutRef.current) {
                clearTimeout(checkStatusTimeoutRef.current);
                checkStatusTimeoutRef.current = null;
            }
            isCheckingStatusRef.current = false;
            setLoadingTTSGeneration(false);
            setWaitingRank(null);
            setShowStatusModal(false);
            setGeneratingParentId(null);
            // Nothing is reloaded: the stored state is back to what it was before this run, and
            // the capsules on screen keep what the user typed, ready to be relaunched.
        }).finally(() => {
            cancelRequestedRef.current = false;
        });
    }, [generatingParentId, cancelGeneration, setLoadingTTSGeneration]);

    const handleStatusModalClose = useCallback(() => {
        setShowStatusModal(false);

        if (errorReason !== "statusUnavailable") {
            return;
        }

        const parentToOpen = statusUnavailableParentIdRef.current;
        statusUnavailableParentIdRef.current = null;

        // Hard load so bootstrap/content refetch cleanly after a status-poll outage.
        if (parentToOpen) {
            window.location.assign(`/create/text-to-speech/${parentToOpen}`);
        } else {
            window.location.reload();
        }
    }, [errorReason]);

    // Memoized callback to update currentTextObject from Formik values
    const handleTextObjectChange = useCallback((textObject: string) => {
        setCurrentTextObject(textObject);
    }, []);

    return (
        <div className={`${styles.page}${audioUrl ? ` ${styles.pageWithAudio}` : ""}`}>
            {!bootstrapReady ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <LoadingSpinner />
                </div>
            ) : (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    key={`${i18n.language}-${id || "new"}`}
                    validateOnMount
                >
                    {({ isSubmitting, submitForm, errors, values, setValues, setFieldValue }) => {
                        // Store setValues in ref so we can call it from checkAllAudioStatus
                        // Update ref directly (no useEffect needed in render function)
                        setValuesRef.current = (vals) => setValues((prev) => ({ ...prev, ...vals }));

                        const isDialogueModel = supportsDialogue(values.audioModel);
                        const modelCharLimit = getModelCharLimit(values.audioModel);

                        // Filter voices based on selected model (only in dialog mode)
                        const getFilteredVoiceOptions = () => {
                            // If voices are not loaded yet, return voiceOptions (even if empty) to avoid filtering issues
                            if (!voiceOptions || voiceOptions.length === 0) {
                                return voiceOptions;
                            }

                            // Only filter voices in dialog mode (multiple narrators or multiple dialog lines)
                            const isDialogMode = values.useMultipleNarrators || values.dialogLines.length > 1;

                            // In single text mode, show all voices regardless of model
                            if (!isDialogMode) {
                                return voiceOptions;
                            }

                            if (!values.audioModel) {
                                return voiceOptions;
                            }

                            const isElevenLabs = isElevenLabsModel(values.audioModel);
                            let filtered: any[] = [];

                            if (isDialogueModel && isDialogMode) {
                                // Only show ElevenLabs voices, cloned voices, and prompt voices (all treated as ElevenLabs)
                                filtered = voiceOptions.filter(
                                    (voice) =>
                                        voice.origin === "elevenlabs" ||
                                        (voice as any)?.isCloned === true ||
                                        (voice as any)?.isPromptVoice === true,
                                );
                            } else {
                                filtered = voiceOptions;
                            }

                            // If filtering resulted in empty array but we have voices, don't filter (show all voices)
                            // This can happen if voices are loaded but don't match the model yet
                            if (filtered.length === 0 && voiceOptions.length > 0 && values.audioModel) {
                                // Return all voices if filtered result is empty - this prevents "0 voices" issue
                                // The voice compatibility check will happen when user selects a voice
                                return voiceOptions;
                            }

                            // Always include the currently selected voice if it exists, even if it's not in the filtered list
                            // This ensures the dropdown shows the selected voice after refresh
                            const currentSelectedVoice = values.selectedVoice;

                            if (currentSelectedVoice && voiceOptions.length > 0) {
                                const selectedVoiceInOptions = voiceOptions.find(
                                    (voice) => voice.value === currentSelectedVoice,
                                );
                                if (
                                    selectedVoiceInOptions &&
                                    !filtered.find((voice) => voice.value === currentSelectedVoice)
                                ) {
                                    // Check if the selected voice is compatible with the model
                                    const isCompatible =
                                        (isElevenLabs && selectedVoiceInOptions.origin === "elevenlabs") ||
                                        (values.audioModel === "OPENAI" && selectedVoiceInOptions.origin === "openai");

                                    if (isCompatible) {
                                        filtered = [selectedVoiceInOptions, ...filtered];
                                    }
                                }
                            }

                            return filtered;
                        };

                        const filteredVoiceOptions = getFilteredVoiceOptions();

                        // Single source of truth for conversation mode
                        const isConversation = values.podcastType === "conversation";

                        // Always filter out selectedVoice since validation is handled in runtime logic (hasValidVoice)
                        // Also filter out selectedSecondVoice in conversation mode (not used there)
                        const relevantErrors = Object.keys(errors).filter(
                            (key) => key !== "selectedVoice" && (!isConversation || key !== "selectedSecondVoice"),
                        );

                        // Explicit voice check helper
                        const hasValidVoice = () => {
                            if (isConversation) {
                                // In conversation mode, check all capsules have valid voices in their segments
                                return capsules.every((capsule) => {
                                    // If capsule has text segments, check each segment has a voice
                                    if (capsule.textSegments && capsule.textSegments.length > 0) {
                                        return capsule.textSegments.every(
                                            (seg) => seg.voiceId && seg.voiceId.trim() !== "",
                                        );
                                    }
                                    // If capsule has dialog lines, check each line has a voice
                                    if (capsule.dialogLines && capsule.dialogLines.length > 0) {
                                        return capsule.dialogLines.every(
                                            (line) => line.voiceId && line.voiceId.trim() !== "",
                                        );
                                    }
                                    // If capsule has plain text, check if a voice is selected in form
                                    if (capsule.textValue && capsule.textValue.trim() !== "") {
                                        return values.selectedVoice && values.selectedVoice.trim() !== "";
                                    }
                                    return true; // Empty capsule is valid
                                });
                            } else {
                                // Narrative: ensure each capsule has a voice (per-capsule, segment, or global fallback)
                                const globalVoice = values.selectedVoice?.trim();

                                return capsules.every((capsule) => {
                                    // Empty capsule is fine
                                    if (!capsule.textValue?.trim()) return true;

                                    // Per-capsule voice is enough
                                    if (capsule.selectedVoice?.trim()) return true;

                                    // Or all segments carry a voice (if user used selections)
                                    if (capsule.textSegments && capsule.textSegments.length > 0) {
                                        const allSegmentsHaveVoice = capsule.textSegments.every((seg) =>
                                            seg.voiceId?.trim(),
                                        );
                                        if (allSegmentsHaveVoice) return true;
                                    }

                                    // Or there's a global voice that can be used as fallback
                                    if (globalVoice) return true;

                                    // No voice available for this capsule
                                    return false;
                                });
                            }
                        };

                        const isDisabled =
                            isSubmitting ||
                            isAudioExhausted ||
                            capsules.length === 0 ||
                            !hasValidVoice() ||
                            !values.textObject ||
                            !values.selectedContentLanguage ||
                            !values.audioModel ||
                            // Check per-capsule character limit for v3 model
                            (modelCharLimit !== undefined &&
                                capsules.some((capsule) => calculateCapsuleCharacters(capsule) > modelCharLimit)) ||
                            // Check all capsules have content
                            !capsules.every((capsule) => {
                                // Only require segments in conversation mode, not in narrative mode with v3
                                if (isConversation) {
                                    // For conversation mode, require text segments with voice assignments
                                    if (!capsule.textSegments || capsule.textSegments.length === 0) {
                                        return false; // Must have at least one voice assigned
                                    }
                                    // All segments must have text and voice
                                    return capsule.textSegments.every((seg) => {
                                        const segmentText = capsule.textValue
                                            ? capsule.textValue.substring(seg.startIndex, seg.endIndex).trim()
                                            : "";
                                        return segmentText !== "" && seg.voiceId && seg.voiceId.trim() !== "";
                                    });
                                }

                                // For narrative mode, validate plain text
                                // (ignore textSegments in narrative mode - they're only relevant in conversation)
                                return capsule.textValue && capsule.textValue.trim() !== "";
                            }) ||
                            relevantErrors.length > 0 ||
                            loadingTTSGeneration;

                        // Generate tooltip message for disabled button
                        const getDisabledTooltipMessage = (): string => {
                            if (loadingTTSGeneration) {
                                return "";
                            }
                            if (isSubmitting) {
                                return "";
                            }
                            if (isAudioExhausted) {
                                return t("quotaReachedAudioTooltip");
                            }

                            const reasons: string[] = [];

                            // Check for capsules
                            if (capsules.length === 0) {
                                reasons.push(t("noCapsules") || "At least one capsule is required");
                            }

                            // Check all capsules have valid content
                            const globalVoice = values.selectedVoice?.trim();
                            capsules.forEach((capsule, index) => {
                                // Check character limit per capsule for v3 model
                                if (modelCharLimit !== undefined) {
                                    const capsuleCharCount = calculateCapsuleCharacters(capsule);
                                    if (capsuleCharCount > modelCharLimit) {
                                        reasons.push(
                                            `${t("capsule") || "Capsule"} ${index + 1}: ${
                                                t("tooManyCharacters") || "Too many characters"
                                            } (${capsuleCharCount}/${modelCharLimit})`,
                                        );
                                    }
                                }

                                // Check voice for each capsule
                                if (capsule.textValue?.trim()) {
                                    const hasCapsuleVoice = Boolean(capsule.selectedVoice?.trim());
                                    const hasSegmentVoices =
                                        capsule.textSegments && capsule.textSegments.length > 0
                                            ? capsule.textSegments.every((seg) => seg.voiceId?.trim())
                                            : false;
                                    const canUseGlobalVoice = Boolean(globalVoice);

                                    if (!hasCapsuleVoice && !hasSegmentVoices && !canUseGlobalVoice) {
                                        reasons.push(
                                            `${t("capsule") || "Capsule"} ${index + 1}: ${
                                                t("voiceRequired") || "Voice is required"
                                            }`,
                                        );
                                    }
                                }

                                // Only require segments in conversation mode, not in narrative mode with v3
                                if (isConversation) {
                                    if (!capsule.textSegments || capsule.textSegments.length === 0) {
                                        reasons.push(
                                            `${t("capsule") || "Capsule"} ${index + 1}: ${
                                                t("pleaseSelectVoiceFirst") ||
                                                "Please select a voice first by highlighting text"
                                            }`,
                                        );
                                    } else if (
                                        !capsule.textSegments.every((seg) => {
                                            const segmentText = capsule.textValue
                                                ? capsule.textValue.substring(seg.startIndex, seg.endIndex).trim()
                                                : "";
                                            return segmentText !== "" && seg.voiceId;
                                        })
                                    ) {
                                        reasons.push(
                                            `${t("capsule") || "Capsule"} ${index + 1}: ${
                                                t("allDialogLinesMustHaveTextAndVoice") ||
                                                "All text segments must have text and voice"
                                            }`,
                                        );
                                    }
                                } else if (!capsule.textValue || capsule.textValue.trim() === "") {
                                    // For narrative mode, only check that text exists (ignore segments)
                                    reasons.push(
                                        `${t("capsule") || "Capsule"} ${index + 1}: ${t("textRequired") || "Text is required"}`,
                                    );
                                }
                            });

                            if (!values.textObject) {
                                reasons.push(t("titleRequired") || "Title is required");
                            }
                            if (!values.selectedContentLanguage) {
                                reasons.push(t("languageRequired") || "Language is required");
                            }
                            if (!values.audioModel) {
                                reasons.push(t("modelRequired") || "Model is required");
                            }

                            if (relevantErrors.length > 0 && reasons.length === 0) {
                                const errorMessages = Object.values(errors)
                                    .filter((error) => error)
                                    .map((error) => (typeof error === "string" ? error : String(error)));
                                reasons.push(...errorMessages);
                            }

                            if (reasons.length > 0) {
                                return t("formIncompleteTooltip", { fields: reasons.join(", ") }) || reasons.join(", ");
                            }

                            return "";
                        };

                        const tooltipMessage = getDisabledTooltipMessage();

                        const wordCount = calculateWordCount(capsules);

                        // Calculate character count based on focused capsule or total
                        const charCount = (() => {
                            if (modelCharLimit !== undefined && focusedCapsuleId) {
                                const focusedCapsule = capsules.find((c) => c.id === focusedCapsuleId);
                                return focusedCapsule ? calculateCapsuleCharacters(focusedCapsule) : 0;
                            }
                            return calculateTotalCharacters(capsules);
                        })();

                        // Check if current focused capsule exceeds the model's character limit (ElevenLabs V3/V4)
                        const isCharLimitExceeded =
                            modelCharLimit !== undefined && focusedCapsuleId ? charCount > modelCharLimit : false;

                        return (
                            <>
                                <TextObjectSync onTextObjectChange={handleTextObjectChange} />
                                <VoiceErrorCleanup />
                                <div className={styles.work}>
                                    <section className={styles.editor}>
                                        <div className={styles.editorHead}>
                                            <div>
                                                <h1>{t("textToSpeech")}</h1>
                                                <p>{t("writeTheStoryYouWanToRecord")}</p>
                                            </div>
                                            {remainingWords != null && (
                                                <span className={styles.token}>
                                                    <Icon name="ic-volume" />
                                                    {t("remainingWords")}&nbsp;:{" "}
                                                    <b>{remainingWords.toLocaleString()}</b>
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.editorBody}>
                                            <MainEditorSection
                                                t={t}
                                                isDialogueModel={isDialogueModel}
                                                values={values}
                                                filteredVoiceOptions={filteredVoiceOptions}
                                                selectedVoices={selectedVoices}
                                                capsules={capsules}
                                                onCapsuleTitleChange={handleCapsuleTitleChange}
                                                onCapsuleRemove={handleRemoveCapsule}
                                                getCapsuleTextChangeHandler={getCapsuleTextChangeHandler}
                                                getCapsuleSegmentsChangeHandler={getCapsuleSegmentsChangeHandler}
                                                getCapsuleSelectionHandler={getCapsuleSelectionHandler}
                                                getCapsuleAssignVoiceHandler={getCapsuleAssignVoiceHandler}
                                                onAddCapsule={handleAddCapsule}
                                                onCapsuleFocus={setFocusedCapsuleId}
                                                focusedCapsuleId={focusedCapsuleId}
                                                onGlobalVoiceApply={handleGlobalVoiceApply}
                                                lastAppliedVoice={lastAppliedVoice}
                                                selectedAudioId={selectedAudioId}
                                                onAudioSelect={handleAudioSelect}
                                                routeId={id}
                                                buttons={null}
                                            />
                                        </div>
                                        <div className={styles.editorFoot}>
                                            <GenerateActionsSection
                                                t={t}
                                                wordCount={wordCount}
                                                charCount={charCount}
                                                charLimit={modelCharLimit}
                                                isDisabled={isDisabled}
                                                tooltipMessage={tooltipMessage}
                                                loadingTTSGeneration={loadingTTSGeneration}
                                                submitForm={submitForm}
                                                waitingRank={waitingRank}
                                                audioUrl={audioUrl}
                                                audioTitle={audioTitle}
                                                generationStatus={generationStatus}
                                                onNavigateToTranslation={() => navigate("/create/translation")}
                                                onOpenProjectModal={() => {
                                                    const capsulesWithAudio = capsules.filter(
                                                        (cap) => cap.audioUrl && (cap.audioId || cap.id),
                                                    );
                                                    if (capsulesWithAudio.length > 1) {
                                                        setIsVariantsModalOpen(true);
                                                    } else {
                                                        setIsModalOpen(true);
                                                    }
                                                }}
                                                capsuleProjectInfo={capsuleProjectInfo}
                                                isCharLimitExceeded={isCharLimitExceeded}
                                                audioOptions={buildAudioOptions()}
                                                selectedAudioId={selectedAudioId}
                                                onAudioSelect={handleAudioSelect}
                                                focusedCapsuleId={focusedCapsuleId}
                                                capsules={capsules}
                                            />
                                        </div>
                                    </section>

                                    <aside className={styles.params}>
                                        <div className={styles.paramsHead}>
                                            <Icon name="ic-settings" />
                                            {t("settings")}
                                        </div>
                                        <div className={styles.rightSidebar}>
                                            <TextToSpeechForm
                                                handleAudioAIAwaiting={(
                                                    audioId: string,
                                                    setValuesFn: (values: Partial<FormValues>) => void,
                                                ) =>
                                                    handleAudioAIAwaiting(audioId, (formValues) => {
                                                        setValues((prevValues) => ({
                                                            ...prevValues,
                                                            ...formValues,
                                                        }));
                                                    })
                                                }
                                                id={id}
                                                voiceOptions={filteredVoiceOptions}
                                                audioSpeed={audioSpeed}
                                                onAudioSpeedChange={handleSpeedChange}
                                                onUpdateLineVoice={(lineId: string, voiceId: string) => {
                                                    setValues({
                                                        ...values,
                                                        dialogLines: values.dialogLines.map((line) =>
                                                            line.id === lineId ? { ...line, voiceId } : line,
                                                        ),
                                                    });
                                                }}
                                                selectedVoices={selectedVoices}
                                                onAddVoice={handleAddVoice}
                                                onRemoveVoice={handleRemoveVoice}
                                                hasTextSelection={hasTextSelection}
                                                onAssignVoiceToSelection={(voiceId: string) => {
                                                    setLastAppliedVoice(voiceId);
                                                    if (
                                                        activeCapsuleId &&
                                                        capsuleAssignVoiceHandlers[activeCapsuleId]
                                                    ) {
                                                        capsuleAssignVoiceHandlers[activeCapsuleId](voiceId);
                                                    }
                                                }}
                                                lastAppliedVoice={lastAppliedVoice}
                                                focusedCapsuleId={focusedCapsuleId}
                                                capsules={capsules}
                                                onCapsuleVoiceChange={handleCapsuleVoiceChange}
                                                onCapsuleSpeedChange={handleCapsuleSpeedChange}
                                                onCapsuleModelChange={handleCapsuleModelChange}
                                                selectedAudioId={selectedAudioId}
                                            />
                                        </div>
                                    </aside>
                                </div>

                                <AddToProjectModal
                                    isOpen={isModalOpen}
                                    onClose={() => setIsModalOpen(false)}
                                    onAddToProject={handleAddToProject}
                                    placeholder={t("addAudioToProject")}
                                />

                                <SelectVariantsModal
                                    isOpen={isVariantsModalOpen}
                                    onClose={() => setIsVariantsModalOpen(false)}
                                    onAddToProject={handleAddToProject}
                                    variants={capsules
                                        .filter((cap) => cap.audioUrl && (cap.audioId || cap.id))
                                        .map((cap) => {
                                            const capsuleId = cap.audioId || cap.id;
                                            const projectInfo = capsuleProjectInfo[capsuleId] || {
                                                isInProject: false,
                                            };
                                            return {
                                                id: capsuleId,
                                                title: cap.textObject || "",
                                                isInProject: projectInfo.isInProject,
                                                projectName: projectInfo.projectName,
                                            };
                                        })}
                                    placeholder={t("selectVariantsToAdd")}
                                />
                            </>
                        );
                    }}
                </Formik>
            )}

            <AIGenerationStatusModal
                isOpen={showStatusModal}
                onClose={handleStatusModalClose}
                status={generationStatus}
                rank={waitingRank}
                errorTitle={
                    errorReason === "quota"
                        ? t("quotaReachedTitle")
                        : errorReason === "statusUnavailable"
                          ? t("audioStatusUnavailableTitle")
                          : errorReason === "generic"
                            ? t("audioGenerationFailed")
                            : undefined
                }
                errorSubtitle={
                    errorReason === "quota"
                        ? t("quotaReachedAudioSubtitle")
                        : errorReason === "statusUnavailable"
                          ? t("audioStatusUnavailableSubtitle")
                          : errorReason === "generic"
                            ? t("audioGenerationFailedSubtitle")
                            : undefined
                }
                onCancel={generatingParentId ? handleCancelGeneration : undefined}
                cancelling={cancelling}
            />
        </div>
    );
};

export default TextToSpeech;
